export type HelpEntry = {
  simple: string;
  detailed: string;
  /** Numbered procedure for Help dialog topics and screenshot guides. */
  steps?: string[];
  /** Parallel to steps — help content keys for drill-down links. */
  stepHelpKeys?: string[];
};

const helpContentDefinitions = {
  // Category operations
  'add-category': {
    simple: 'Add category',
    detailed:
      'A new category is added under a branch that already has at least two subcategories. Branch folders hold subcategories; content categories hold text rows. The new child is always a content category. Use Split category later if you need subfolders.',
    steps: [
      'Select a branch folder with at least two subcategories',
      'Click the "+" button in the toolbar or Add Category in the context menu',
      'Enter a name and description',
      'Click Add. The new content category appears under that branch',
    ],
  },
  'add-category-disabled': {
    simple: 'Cannot add category',
    detailed:
      'Select a branch with at least two subcategories. To add folders under a content category, use Split category first.'
  },
  'delete-category': {
    simple: 'Delete category',
    detailed:
      'Removes the selected category and its entire branch, all child categories and content rows beneath it.',
    steps: [
      'Select the category (or categories) you want to remove',
      'Click Delete in the toolbar, or choose Delete from the category context menu',
      'Or press Delete or Backspace when you are not typing in a text field or the content editor',
      'Confirm in the dialog',
    ],
  },
  'delete-category-disabled': {
    simple: 'Cannot delete category',
    detailed: 'No categories are currently selected. Select one or more categories to delete them.'
  },
  'rename-category': {
    simple: 'Rename category',
    detailed:
      'Changes the selected category name and description in the category structure. Use Rename in the toolbar or the category context menu, edit the name and description fields, and confirm. Optional: in Settings, enable “Click category name to rename” for inline name edit after the category is already selected (Finder-style click on the name). Add or change the description in the Rename dialog.',
    steps: [
      'Select the category you want to rename',
      'Click Rename in the toolbar or choose Rename from the category context menu',
      'Enter the new name and add or edit the description',
      'Click Rename to confirm',
    ],
  },
  'rename-category-disabled': {
    simple: 'Cannot rename category',
    detailed: 'No category is currently selected. Select a category to rename it.'
  },
  'move-category': {
    simple: 'Move category',
    detailed:
      'Reorders categories among siblings under the same parent. In the category structure, select one category. In the content pane, select multiple direct children with ⌘/Ctrl+click or Shift+click; the selection must be consecutive siblings and cannot include every child. Keyboard: ↑/↓ when focus is outside the category structure, or ⌘/Ctrl+↑/↓ anytime outside text fields.',
    steps: [
      'Select one category in the category structure, or consecutive siblings in the content pane',
      'Use Move up / Move down in the toolbar, or ↑/↓ when focus is outside the category structure, or ⌘/Ctrl+↑/↓ outside text fields',
    ],
  },
  'drag-drop-categories': {
    simple: 'Drag and drop categories',
    detailed:
      'Repositions categories by dragging in the category structure or content pane. Drop before or after to reorder siblings under the same parent; drop inside a branch folder to move under a new parent.',
    steps: [
      'Select one category in the category structure, or one or more in the content pane',
      'Drag the category in the category structure or a row in the content pane',
      'Drop before or after a sibling to reorder under the same parent',
      'Drop inside a branch folder to move the category under that parent',
      'Hold ⌥ Alt (Option on Mac) while dropping to duplicate where supported',
    ],
  },
  'move-category-up': {
    simple: 'Move category up',
    detailed:
      'Move the selected category or consecutive content-pane selection up among siblings under the same parent. In the category structure, one category is selected. Keyboard: ↑ when focus is outside the category structure, or ⌘/Ctrl+↑ anytime outside text fields (including when the category structure is focused).'
  },
  'move-category-down': {
    simple: 'Move category down',
    detailed:
      'Move the selected category or consecutive content-pane selection down among siblings under the same parent. In the category structure, one category is selected. Keyboard: ↓ when focus is outside the category structure, or ⌘/Ctrl+↓ anytime outside text fields (including when the category structure is focused).'
  },
  'move-category-up-disabled': {
    simple: 'Cannot move category',
    detailed: 'Select categories that can move in this direction, or try the opposite arrow.'
  },
    'move-category-down-disabled': {
      simple: 'Cannot move category',
      detailed: 'Select categories that can move in this direction, or try the opposite arrow.'
  },
  'move-content-up': {
   simple: 'Move content up',
   detailed: 'Move the selected content up in the list. This changes the order within the same parent category. Keyboard: ↑ when focus is outside the category structure, or ⌘/Ctrl+↑ anytime outside text fields (including when the category structure is focused).'
 },
  'move-content-down': {
     simple: 'Move content down',
     detailed: 'Move the selected content down in the list. This changes the order within the same parent category. Keyboard: ↓ when focus is outside the category structure, or ⌘/Ctrl+↓ anytime outside text fields (including when the category structure is focused).'
 },
   'move-content-up-disabled': {
     simple: 'Cannot move content',
     detailed: 'Select content rows that can move in this direction, or try the opposite arrow.'
 },
 'move-content-down-disabled': {
     simple: 'Cannot move content',
     detailed: 'Select content rows that can move in this direction, or try the opposite arrow.'
  },
  'copy-category': {
    simple: 'Copy category',
    detailed: 'Create a duplicate of this category with all its contents. The copied category will be placed in your clipboard for pasting elsewhere.'
  },
  'copy-category-disabled': {
    simple: 'Cannot copy category',
    detailed: 'No categories are currently selected. Select one or more categories to copy them.'
  },
  'cut-category': {
    simple: 'Cut category',
    detailed:
      'Remove selected categories from the category structure and place them on the clipboard. Paste only within the same document (copy works across open tabs).'
  },
  'cut-category-disabled': {
    simple: 'Cannot cut category',
    detailed: 'No categories are currently selected. Select one or more categories to cut them.'
  },
  'paste-category': {
    simple: 'Paste category',
    detailed: 'Insert a previously copied or cut category at this location. The category will maintain its structure and all contained content.'
  },
  'paste-category-disabled': {
    simple: 'Cannot paste category',
    detailed:
      'Select a branch with at least two subcategories to paste categories, or a content category for content rows. Clear category and list multi-selection first. Cut paste works within the same document; copy paste works across open tabs.'
  },
  'cut-copy-paste-categories': {
    simple: 'Cutting, copying, and pasting categories',
    detailed:
      'Cut moves categories within the same document. Copy duplicates to the clipboard for pasting in this document or another open tab. Press Escape to cancel a pending cut or copy.',
    steps: [
      'Select one or more categories in the category structure',
      'Cut: move within the same document (toolbar or ⌘/Ctrl+X)',
      'Copy: duplicate to the clipboard; paste here or in another open document tab (toolbar or ⌘/Ctrl+C)',
      'Select a destination branch that already has at least two subcategories',
      'Paste: insert from the clipboard (toolbar or ⌘/Ctrl+V); pasting two or more categories at once requires at least two on the clipboard',
    ],
  },
  'cut-content': {
    simple: 'Cut content',
    detailed:
      'Remove the selected content rows from their category and put them on the clipboard. Cut content can only be pasted within the same document.'
  },
  'cut-content-disabled': {
    simple: 'Cannot cut content',
    detailed: 'No content rows are selected. Select items in the content list, or use Cut on a category in the category structure.'
  },
  'copy-content': {
    simple: 'Copy content',
    detailed:
      'Copy the selected content rows to the clipboard. You can paste them into a content category in this document or another open document.'
  },
  'copy-content-disabled': {
    simple: 'Cannot copy content',
    detailed: 'No content rows are selected. Select items in the content list, or use Copy on a category in the category structure.'
  },
  'paste-content': {
    simple: 'Paste content',
    detailed:
      'Insert the content rows from your clipboard into the selected content category. Duplicate subcase/text pairs in that category are blocked.'
  },
  'paste-content-disabled': {
    simple: 'Cannot paste content',
    detailed:
      'Put content rows on the clipboard, select a single content category in the category structure, and clear multi-selection in the category structure and list. Cut paste works within the same document; copy paste works across open tabs.'
  },
  'clear-clipboard': {
    simple: 'Cancel cut or copy (Escape)',
    detailed:
      'Press Escape outside text fields, the editor, and open menus to clear the clipboard without pasting. Pending cut items return to normal appearance.',
  },
  'paste-empty': {
    simple: 'Paste',
    detailed: 'Paste a copied or cut category or content rows at the current selection.'
  },
  'paste-empty-disabled': {
    simple: 'Cannot paste',
    detailed: 'Nothing is in the clipboard. Copy or cut a category or content rows first.'
  },
  'import-subtree-from-document': {
    simple: 'Import branch from document',
    detailed:
      'Clones a branch from another document under your selection. Choose a different document as the source. You can also copy in one tab, switch tabs, and paste to duplicate across documents.',
    steps: [
      'Select a branch with at least two subcategories in the current document',
      'Click Import branch from document (folder-upload icon next to Paste) or use the category context menu',
      'Choose a source document from your library (other than the open document)',
      'Pick the root category of the branch to copy, it and all content beneath it are cloned under your selection',
    ],
  },
  'import-subtree-from-document-disabled': {
    simple: 'Import branch from document (unavailable)',
    detailed:
      'Select a single branch folder with at least two subcategories. Clear multi-selection in the category structure and content list first.'
  },

  // Content operations
  'add-content': {
    simple: 'Add content',
    detailed:
      'Adds a new row to the selected content category. With auto-paste enabled in Settings, clipboard text may appear when you open the editor.',
    steps: [
      'Select a content category (a leaf category that holds text rows)',
      'Click Add Content in the toolbar',
      'Enter your text and choose or create a subcase',
      'Save. The new row is added to the category',
    ],
  },
  'add-content-disabled': {
    simple: 'Cannot add content',
    detailed:
      'Select a content category, a leaf category that holds text rows. For a branch folder, use Split category first or add content under one of its content subcategories.'
  },
  'edit-content': {
    simple: 'Edit content',
    detailed:
      'Opens the rich-text editor for the selected row. Use Save or Cancel when you are done; most changes persist automatically while you edit.',
    steps: [
      'Select a single content row in the category',
      'Click Edit Content in the toolbar',
      'Modify text, subcase, and formatting as needed',
      'Changes save automatically; use Save or click away when finished',
    ],
  },
  'edit-content-disabled': {
    simple: 'Cannot edit content',
    detailed: 'No content is currently selected for editing. Select a single content item first.'
  },
  'adjust-text-content': {
    simple: 'Adjust text',
    detailed:
      'Rewrites a single plain-text row by borrowing text from an adjacent row with the same subcase: prepend a passage from the end of the previous row and/or append a passage from the start of the next row. Mark a spot in a neighbour row to select up to its end (previous) or from its start (next), then Prepend/Append and Save. A “[...]” line is inserted after the prepended passage and before the appended one to mark each borrowed boundary. Only this row’s text changes — the neighbouring rows are left untouched, and the edit can be reverted with Undo. Available only for plain-text rows (not rich-text/media rows), and only when no subcase filter is active.',
    steps: [
      'Clear any subcase filter, then select a single plain-text row that has an adjacent row of the same subcase above or below it',
      'Click Adjust text in the toolbar (next to Add/Edit Content), or in the content panel header, or right-click the row and choose “Adjust text…”',
      'In the previous row, click where the passage to borrow begins (it marks to the end), then click Prepend',
      'In the next row, click where the passage to borrow ends (it marks from the start), then click Append',
      'Review the preview of this row and click Save (use Undo afterwards to revert)',
    ],
  },
  'adjust-text-content-disabled': {
    simple: 'Cannot adjust text',
    detailed:
      'Adjust text needs a single plain-text row with an adjacent row of the same subcase directly above or below it, and no active subcase filter. Rich-text/media rows and multi-row selections are not supported.'
  },
  'content-panel-editing-active': {
    simple: 'Editor is open',
    detailed: 'Save or cancel in the editor below before using Add or Edit Content again.'
  },
  'delete-content': {
    simple: 'Delete content',
    detailed: 'Removes the selected content rows from the category.',
    steps: [
      'Select one or more content rows',
      'Click Delete in the toolbar, or press Delete/Backspace when focus is outside text fields and the editor',
      'Confirm deletion in the dialog',
    ],
  },
  'move-content': {
    simple: 'Move content',
    detailed:
      'Reorders content rows within the same content category. Select one or more consecutive rows in the content pane. Keyboard: ↑/↓ when focus is outside the category structure, or ⌘/Ctrl+↑/↓ anytime outside text fields.',
    steps: [
      'Select one or more consecutive content rows in the content pane',
      'Use Move up / Move down in the toolbar, or ↑/↓ when focus is outside the category structure, or ⌘/Ctrl+↑/↓ outside text fields',
    ],
  },
  'drag-drop-content': {
    simple: 'Drag and drop content',
    detailed:
      'Repositions content rows by dragging in the content pane or onto the category structure. Drop before or after a row to reorder within the same category; drop inside a content category in the category structure to move rows to that category.',
    steps: [
      'Select one or more content rows in the content pane',
      'Drag the selected rows',
      'Drop before or after another row to reorder within the same content category',
      'Drop inside a content category in the category structure to move rows to that category',
      'Hold ⌥ Alt (Option on Mac) while dropping on the category structure to copy instead of move',
    ],
  },
  'cut-copy-paste-content': {
    simple: 'Cutting, copying, and pasting content',
    detailed:
      'Cut moves content rows within the same document. Copy duplicates to the clipboard for pasting in this document or another open tab. Press Escape to cancel a pending cut or copy.',
    steps: [
      'Select one or more content rows in the content pane',
      'Cut: move within the same document (toolbar or ⌘/Ctrl+X)',
      'Copy: duplicate to the clipboard; paste here or in another open document tab (toolbar or ⌘/Ctrl+C)',
      'Select a single content category in the category structure as the destination',
      'Paste: insert from the clipboard (toolbar or ⌘/Ctrl+V); duplicate subcase/text pairs in that category are blocked',
    ],
  },
  'delete-content-disabled': {
    simple: 'Cannot delete content',
    detailed: 'No content is currently selected. Select one or more content items to delete them.'
  },
  
  // Document operations
  'document-menu': {
    simple: 'Document menu',
    detailed:
      'Open document-wide actions: New.., Open, Close, Delete, Rename.., Duplicate.., Merge.., Compare.. (when enabled), Share with user.. (when enabled), New document from files (AI).., Add to document from files (AI).., and Import .nbr archive.. Select a document in the panel first for most items. Right-click a document row for the same actions; Compare… opens the compare dialog (owned document pre-filled on the left when you right-click it).'
  },
  'new-document': {
    simple: 'New document',
    detailed:
      'Create a new document in your library. It opens in a new tab with the document root as a content category, add rows there, or use Split category or Structure builder to build folders before Add category (+) is available on a branch.',
    steps: [
      'Open the Document Menu in the document panel',
      'Click "New.."',
      'Enter a unique name for your new document',
      'Click "Create", the document opens in a new tab; the root is a content category ready for rows or splitting',
    ],
  },
  'create-document-options': {
    simple: 'Ways to create a document',
    detailed:
      'Several paths add a document to your library or open a new tab. Choose based on whether you start empty, from files, from an existing document, or from a category branch.',
    steps: [
      'New.. — Document Menu → New.. (empty document with root content category)',
      'Import .nbr archive.. — Document Menu → Import .nbr archive.. (legacy Narralyzer desktop .nbr backup)',
      'New document from files (AI).. — Document Menu → upload PDF, Word, or text; AI proposes categories and content',
      'Duplicate.. — copy a document you own (Document Menu or right-click on a closed document)',
      'Duplicate as document — copy a selected category branch into a new library document (toolbar or category context menu)',
      'Structure builder — with no document tab: Instructions (AI) or Import tab can create a document and category structure',
      'Merge.. → merge into a new document — combine two documents under a new name (Document Menu → Merge..)',
    ],
    stepHelpKeys: [
      'new-document',
      'load-from-file',
      'suggest-tree',
      'duplicate-document',
      'duplicate-as-document',
      'tree-assistant',
      'merge-document',
    ],
  },
  'open-document': {
    simple: 'Open document',
    detailed:
      'You can work on multiple owned documents in tabs at once. Single-click an open row or editor tab to switch the active tab without closing others.',
    steps: [
      'Single-click a closed document in Closed Documents to select it',
      'Open it by double-clicking the row, clicking its document icon, Document Menu → Open, or right-click → Open',
      'The document appears as an editor tab and its categories load',
    ],
  },
  'open-document-disabled': {
    simple: 'Cannot open document',
    detailed: 'This document is already open. Switch to it from Open Documents or its editor tab.'
  },
  'close-document': {
    simple: 'Close document',
    detailed: 'Closing moves the document to Closed Documents; it stays in your library. Changes save automatically while open.',
    steps: [
      'Select an open document in Open Documents or click its editor tab',
      'Close via Document Menu → Close, the document icon on the open row, or the tab ×',
      'The document moves to Closed Documents and remains in your library',
    ],
  },
  'close-document-disabled': {
    simple: 'Cannot close document',
    detailed: 'Select a row under Open Documents or click an editor tab for an open document.'
  },
  'delete-document': {
    simple: 'Delete document',
    detailed: 'Permanently removes the document from your library. Available only for documents you own.',
    steps: [
      'Select one of your own documents in the Document Panel',
      'Document Menu → Delete or right-click → Delete',
      'Confirm in the dialog',
    ],
  },
  'delete-document-disabled': {
    simple: 'Cannot delete document',
    detailed: 'Select one of your own documents in the panel.'
  },
  'rename-document': {
    simple: 'Rename document',
    detailed:
      'Change the name of a document you own. Pick a unique name in the dialog, it updates in Open Documents, Closed Documents, and editor tabs.',
    steps: [
      'Select one of your own documents in the Document Panel',
      'Document Menu → Rename.. Or right-click → Rename..',
      'Enter a unique new name and confirm',
    ],
  },
  'rename-document-disabled': {
    simple: 'Cannot rename document',
    detailed: 'Select one of your own documents in the panel.'
  },
  'duplicate-document': {
    simple: 'Duplicate document',
    detailed:
      'Make a full copy in your library with the same category structure and content. Works for documents you own and for view-only shared documents (the source is not changed). Choose which subcases to include and give the copy a new name.',
    steps: [
      'Open a document you own, or a view-only shared tab, or right-click a view-only share under View only shared with me',
      'Document Menu → Duplicate.., right-click → Duplicate.., or Duplicate to my library.. on a view-only share',
      'Choose which subcases to include and enter a name for the new document',
      'Confirm. The copy appears in your document list as an editable document you own',
    ],
  },
  'duplicate-document-disabled': {
    simple: 'Cannot duplicate document',
    detailed: 'Select a document or open a view-only shared document first.'
  },
  'share-document': {
    simple: 'Share with user',
    detailed:
      'Send a document you own to another Narralyze user. With Copy to library, they receive their own editable copy and accept it from Shared with me. With Live view only, they open a read-only tab from View only shared with me; you can revoke access anytime under View only shared by me.',
    steps: [
      'Select one of your own documents in the Document Panel',
      'Document Menu → Share with user.. Or right-click the document',
      'Choose Copy to library or Live view only',
      'Enter the recipient username and confirm',
    ],
  },
  'share-document-disabled': {
    simple: 'Cannot share document',
    detailed: 'Select one of your own documents in the panel. Document sharing requires the feature to be enabled for this installation.'
  },
  'export-docx-full-tree': {
    simple: 'Export to Word (DOCX)',
    detailed: 'Export readable output from Category Structure Overview on the category structure.',
    steps: [
      'Open the document and select its root in the category structure',
      'Open Category Structure Overview',
      'Click Print, Export DOCX, or Export PNG (graphical view)',
    ],
  },
  'merge-document': {
    simple: 'Merge document',
    detailed:
      'Combine two documents into one. Choose merge into (a document you own), merge from (your document or a view-only share), or merge into a new document. A shared document works as the merge-from source. To preview differences without merging, use Document Menu → Compare.. instead.',
    steps: [
      'Select a document you own in the Document Panel',
      'Document Menu → Merge..',
      'Choose merge into, merge from (your document or a view-only share), or merge into a new document',
      'Set missing-content and type-mismatch resolution options',
      'Click Merge to combine the documents',
    ],
  },
  'merge-document-disabled': {
    simple: 'Cannot merge document',
    detailed:
      'Merge needs at least two sources: two documents you own, or one of your documents plus a view-only share. Use the shared document as merge-from. Open Merge from the Document menu to choose merge into, merge from, or merge into a new document.'
  },
  'merge-document-from': {
    simple: 'Merge from document',
    detailed:
      'Select the source document to merge from: one of your own documents or a view-only share. Content is copied into your merge target; the shared document stays unchanged on the server.'
  },
  'merge-document-new': {
    simple: 'Merge into new document',
    detailed:
      'Create a new document starting from your merge target, then pick content from the merge source to add. Opens a target workspace (new document on the left, source on the right). Requires Document merge compare. If the new name matches the merge target, Merge runs in place on that document instead.',
  },
  'merge-document-name': {
    simple: 'New document name',
    detailed: 'Enter a name for the new document that will be created from the merge. This name must be unique among your documents.'
  },
  'merge-document-missing-resolution': {
    simple: 'Missing content resolution',
    detailed: 'Choose how to handle content that exists in the source document but not in the target: Copy (add missing content), Skip (ignore missing content), or Abort (stop the merge). In compare, this applies only to recursive branch merge on paired branch categories (or document root merge). One-sided category copy and content-category merge (copy all missing rows) always copy explicitly and ignore this setting.'
  },
  'merge-document-type-resolution': {
    simple: 'Type mismatch resolution',
    detailed: 'Choose how to handle conflicts when content types don\'t match between documents: Keep (preserve target content), Skip (ignore conflicting content), or Abort (stop the merge). In compare, shown only for recursive branch merge on paired branch categories; content-category row copy and one-sided category copy use fixed rules.'
  },
  'merge-document-merge': {
    simple: 'Merge documents',
    detailed: 'Execute the merge operation with the selected settings. This will combine the documents according to your resolution preferences.'
  },
  'merge-document-merge-disabled': {
    simple: 'Cannot merge documents',
    detailed:
      'Choose merge into and merge from documents (when none is selected in the list), pick a source document, and fill the new document name if merging into a new document.'
  },
  'document-merge-compare': {
    simple: 'Document merge compare',
    detailed:
      'Compare two documents side by side. Each structure column shows the document name; a View only badge and tinted header mark a read-only shared side. When building a new document, the left column is the target. Status chips on structure rows show match, missing content, has extra, type conflict, and branch differences. With both sides editable, copy and merge can update either side. With a view-only source, only your document (left) changes.',
  },
  'document-merge-compare-view-only-side': {
    simple: 'View-only compare side',
    detailed:
      'This document is shared with you for view only. You can compare and copy from it into your editable document on the other side; this side cannot be changed from compare.',
  },
  'document-merge-compare-disabled': {
    simple: 'Document merge compare (unavailable)',
    detailed:
      'This installation has document merge compare turned off. Ask an administrator to enable the Document merge compare feature flag. Full document Merge may still be available from Merge..'
  },
  'document-merge-compare-dialog': {
    simple: 'Open compare tab',
    detailed:
      'Opens a compare tab for the Compare and Compare with documents selected here. Missing and Type mismatch are set in the compare view header. Check Into a new document to open a target workspace instead of side-by-side compare on existing files.',
  },
  'document-merge-compare-menu': {
    simple: 'Compare documents',
    detailed:
      'Open two documents side by side to review differences in their category structures and content. Copy individual rows or categories from the detail pane, copy selected subcategories from a one-sided branch, or merge whole branchs. Undo last copy reverses the latest pane copy. For a full automatic merge, use Merge.. instead.',
    steps: [
      'Select an owned document and use Document Menu → Compare.., or right-click a document → Compare…',
      'In the dialog, pick Compare (left) and Compare with (right). The right-clicked owned document is pre-filled on the left; otherwise choose both sides',
      'For a view-only share, right-click the grant → Compare… to pre-fill Compare with, then pick your document on the left',
      'Review structures side by side; adjust Missing and Type mismatch in the compare header',
      'Copy rows or categories from the detail pane, or use Merge to left/right for whole branchs',
      'Use Undo last copy in the compare header to reverse the latest copy',
    ],
  },
  'document-merge-compare-pick-left': {
    simple: 'Compare from context menu',
    detailed:
      'Right-click an owned document and choose Compare… to open the compare dialog with that document on the left (Compare). Pick the second document or view-only share on the right, then click Compare. Same dialog as Document Menu → Compare..'
  },
  'document-merge-compare-with': {
    simple: 'Compare with a view-only share',
    detailed:
      'Right-click a view-only share under Shared with me and choose Compare… to open the compare dialog with that share on the right (Compare with). Pick your owned document on the left, then click Compare.'
  },
  'document-merge-compare-merge-to-left': {
    simple: 'Merge to left / Merge into target',
    detailed:
      'Merges the current selection into the left (Compare) or target document using Missing and Type mismatch from the header. When both merge directions apply to a paired category, this button appears when the right structure is selected (you are sending the selection to the left). For type conflicts on a structure selection, resolves the whole category from the header. In a new-document workspace the button is labeled Merge into target and appears when the source (right) structure is selected, or when either document root is selected for a full structure merge.'
  },
  'document-merge-compare-merge-to-right': {
    simple: 'Merge to right',
    detailed:
      'Merges the current selection into the right (Compare with) document using Missing and Type mismatch from the header. When both directions apply, this button appears when the left structure is selected (you are sending the selection to the right). Only available when both documents are editable (not when the right side is view-only).'
  },
  'document-merge-compare-copy-to-left': {
    simple: 'Copy to left',
    detailed:
      'Copies selected right-only content rows or categories from the detail pane into the left document. For a one-sided branch, check at least two direct subcategories to copy the branch shell plus those branches (with all content). Copy category only creates an empty category when every row is already selected. Use Merge to left in the header to copy an entire one-sided branch at once.'
  },
  'document-merge-compare-copy-to-right': {
    simple: 'Copy to right',
    detailed:
      'Copies selected left-only content rows or categories into the right document. For a one-sided branch, check at least two direct subcategories to copy the branch shell plus those branches. Disabled when the right document is view-only shared with you. Use Merge to right in the header to copy an entire one-sided branch at once.'
  },
  'document-merge-compare-undo-last-copy': {
    simple: 'Undo last action (compare)',
    detailed:
      'Reverses the most recent compare action on the target document: copies (removes created rows/categories) or deletes (restores removed content or categories). Applies to content copies, category copies, selective branch copies, and deletes from the compare pane. Does not undo branch merges or type-conflict merges. There is no redo; document toolbar undo is cleared after a compare mutation.'
  },
  'document-merge-compare-delete-category': {
    simple: 'Delete category (compare)',
    detailed:
      'Right-click a category in the compare structure and choose Delete category to remove it from the editable side without leaving the tab. You can also select the category first; merge actions stay in the compare header. View-only shared sides cannot be edited. Use Undo last action to restore the category branch.'
  },
  'document-merge-compare-branch-selective-copy': {
    simple: 'Selective branch copy (compare)',
    detailed:
      'On a one-sided branch, check at least two subcategories, then Copy to left or Copy to right. Use header Merge to copy the whole branch.',
  },
  'document-merge-compare-branch-content-readonly': {
    simple: 'Branch content not copyable (compare)',
    detailed:
      'Content under a one-sided branch is read-only here. Select the branch in the category structure and use header Merge to copy.',
  },
  'document-merge-compare-select-all-right': {
    simple: 'Select all right-only rows',
    detailed:
      'Select or clear every right-only row in this pane that can be copied to the left document.'
  },
  'document-merge-compare-select-all-left': {
    simple: 'Select all left-only rows',
    detailed:
      'Select or clear every left-only row in this pane that can be copied to the right document.'
  },
  'document-merge-compare-type-conflict-merge': {
    simple: 'Merge type conflict to side',
    detailed:
      'Resolves branch vs content type conflicts by merging into the chosen document using Type mismatch: Keep from the header. In the header, applies to the category selected in the category structure and shows only merge into the opposite side when both directions are possible (select left to merge to the right, and vice versa). In the detail pane, applies to checked type-conflict rows and may offer both directions when the selection supports it. After merging into a side, that side shows Resolved and the same merge direction cannot be applied again; the opposite direction remains available until used.'
  },
  'document-merge-compare-trees': {
    simple: 'Compare structures',
    detailed:
      'Left is Compare (merge target); right is Compare with (source). Click a category to inspect rows in the detail pane. Right-click a category for Delete category and merge actions that match the header. Double-click a branch to expand or collapse; ⌘/Ctrl+double-click toggles the whole branch.'
  },
  'document-merge-compare-detail-pane': {
    simple: 'Compare detail pane',
    detailed:
      'Shows subcategories or content rows for the structure selection. Filter content with Show rows; select one-sided rows to copy. Hover a row to preview or delete on editable sides.',
  },
  'document-merge-compare-content-both': {
    simple: 'On both sides',
    detailed: 'This content row exists on the left and right with the same subcase and text.'
  },
  'document-merge-compare-content-left-only': {
    simple: 'Left content only',
    detailed: 'Row exists only on the left. Select to copy to the right when the right document is editable.'
  },
  'document-merge-compare-content-right-only': {
    simple: 'Right content only',
    detailed: 'Row exists only on the right. Select to copy into the left document.'
  },
  'document-merge-compare-content-one-sided-branch': {
    simple: 'One-sided branch row',
    detailed:
      'Listed from a branch that exists on only one document. These rows are not paired for compare.',
  },
  'document-merge-compare-content-filter': {
    simple: 'Content row filter',
    detailed:
      'Show rows filters the content table by status (Left only, Both, Right only). Resets to All when you change category or pane mode.',
  },
  'structure-categories': {
    simple: 'Structure categories (AI)',
    detailed:
      'AI tool to organize one content category, organize multiple content categories separately, or group sibling categories under new parent folders. Preview the proposal before applying. On a content category, choose a Top-down or Bottom-up building strategy (Bottom-up is the default). On a branch, choose an operation: Create subcategories (build below each content category, with the same Top-down / Bottom-up strategy) or Create parent groups (build above three or more selected siblings).',
    steps: [
      'Content category: select one with at least two rows, open Structure categories (AI), choose strategy and depth, preview, then apply',
      'Optional: use Initial categories (depth 1, bottom-up) for one level of sibling folders on a single content category',
      'Branch or multiple content categories: choose Create subcategories, then Top-down or Bottom-up; after preview, uncheck any subcategory to leave unchanged',
      'Three or more sibling categories: choose Create parent groups to add parent folders above them; after preview, uncheck any proposed parent group to skip',
      'In the content pane, leave branch children unchecked to include all eligible children, or check the exact children to include',
    ],
  },
  'structure-categories-strategy': {
    simple: 'Strategy / operation',
    detailed:
      'For one content category, Bottom-up (default) starts from finer groupings while Top-down builds broader folders first. For a branch or multiple content categories, first choose Create subcategories or Create parent groups. Under Create subcategories, Top-down / Bottom-up is the same clustering strategy applied separately to each selected content category. Create parent groups builds above three or more selected sibling categories.',
  },
  'structure-categories-strategy-top-down': {
    simple: 'Top-down',
    detailed:
      'Builds broader folders first, then subdivides. Good when you want fewer high-level categories with more rows in each before going deeper.',
  },
  'structure-categories-strategy-bottom-up': {
    simple: 'Bottom-up (default)',
    detailed:
      'Starts from finer groupings and merges upward. Good when you want many small leaf categories first, then broader parents above them.',
  },
  'structure-categories-max-depth': {
    simple: 'Maximum depth / height',
    detailed:
      'How many new folder layers the AI may add. For branch bottom-up, this is the max height of new parent levels above selected siblings.',
  },
  'structure-categories-disabled': {
    simple: 'Structure categories (AI) (unavailable)',
    detailed:
      'Select a content category with at least two text rows, or a branch whose direct children are all content buckets. If the branch also contains folder subcategories, check at least two content subcategories in the content pane (document icon) before using Structure categories. Deselect content rows in the table below. If AI category tools are hidden, ask an administrator to enable them.'
  },
  'category-ai-report': {
    simple: 'Generate report (AI)',
    detailed:
      'Qualitative narrative report for the selected content category or branch. The full report — title, section headings, and body — is written in the main language of your document (Hebrew, French, German, etc.), even if optional focus or questions are in another language. To request a different output language, say so explicitly in focus or questions (e.g. "report in English", "rapport en français", "דוח באנגלית"). Structure: overview, main themes (with quotes), then Feedback to the researcher (answers — to your questions when provided, otherwise key takeaways and implications — plus gaps and limitations). After generation, use the Structure diagram switch in the report reader to append a category structure at the end, with Vertical (side-flow) or Horizontal (top-down) layout — same as Category Structure Overview graphical modes. Opens with a title using the category name (optionally suggesting an alternative title). Copy, print, or export to Word (.docx). Optional focus and questions steer selection when the scope is large. Available on view-only shared documents (read-only; does not change the source file).',
    steps: [
      'Select a content category with rows, or a branch that contains content underneath',
      'Click Generate report (AI) in the toolbar or right-click the category and choose Generate report (AI)…',
      'Optionally describe what to emphasize and list questions (one per line)',
      'Generate report; in the reader toolbar toggle Structure diagram on, then choose Vertical or Horizontal layout (same as Category Structure Overview graphical modes)',
      'When the scope has many rows, the most relevant passages are selected automatically',
      'Generate report, then copy, print, or Export DOCX from the reader toolbar',
    ],
  },
  'category-ai-report-disabled': {
    simple: 'Generate report (AI) (unavailable)',
    detailed:
      'Select a content category with at least one row, or a branch that contains content rows underneath. Empty branches and empty content categories are disabled. If the action is hidden, ask an administrator to enable Category report (AI).',
  },
  'generate-subtree': {
    simple: 'Structure categories (AI)',
    detailed:
      'Same as Structure categories (AI): open the dialog to cluster rows into a multi-level branch. Bottom-up is the default; adjust depth there. Legacy name: Split into subcategories (AI).'
  },
  'generate-subtree-disabled': {
    simple: 'Structure categories (AI) (unavailable)',
    detailed: 'See Structure categories (AI) (unavailable).'
  },
  'generate-initial-categories': {
    simple: 'Initial categories preset (in Structure categories)',
    detailed:
      'Inside Structure categories (AI), use “Initial categories (depth 1, bottom-up)” to cluster rows into one level of sibling folders on a single content category. For higher levels afterward, run Structure categories again with top-down or parent-layer grouping.',
  },
  'generate-initial-categories-disabled': {
    simple: 'Initial categories preset (unavailable)',
    detailed:
      'Requires a content category with at least two rows. Use Structure categories (AI) when eligible.',
  },
  'subtree-depth': {
    simple: 'Maximum depth',
    detailed: 'Set the maximum number of levels deep the generated structure can be. Higher values create more detailed hierarchies but may be more complex to manage.'
  },
  'subtree-preview-content': {
    simple: 'Show content preview',
    detailed: 'Display a preview of the content that will be included in each category of the generated structure. This helps you understand how the AI has organized your content.'
  },
  'subtree-use-suggested-name': {
    simple: 'Use AI-suggested name',
    detailed: 'Replace the current category name with the AI-suggested name. This is only available for non-root categories and can help improve the overall structure.'
  },
  'subtree-save-with-content': {
    simple: 'Generate with Content',
    detailed:
      'Create the complete branch structure including all categories and their associated content. Original content is deleted and recreated in the new structure. For branch top-down (multiple content subcategories), applies this to every previewed subcategory.',
  },
  'subtree-save-structure-only': {
    simple: 'Generate Structure Only',
    detailed:
      'Create only the category structure without placing content in the new folders. Original content is preserved in a temporary category for manual organization. For branch top-down (multiple content subcategories), applies this to every previewed subcategory.',
  },
  'subtree-retry': {
    simple: 'Retry with different depth',
    detailed: 'Generate a new structure with the current maximum depth setting. This is useful if you want to try different depth levels to get better organization.'
  },
  'subtree-max-depth': {
    simple: 'Maximum structure depth',
    detailed: 'Set the maximum number of levels deep the generated structure can be. Higher values create more detailed hierarchies but may be more complex to manage.'
  },
  'suggest-tree': {
    simple: 'New document from files (AI)',
    detailed:
      'Review the proposed structure before accepting. Default Content in folders places text in the matching folders. Keep content separate builds folders and stores the original text in Original Files Content. Structure only creates empty folders with no saved content. In Word (.docx) files, black highlight or a near-black character background is imported as [Redacted], or the equivalent in the document’s main language (for example [מצונזר] in Hebrew).',
    steps: [
      'Open the Document Menu',
      'Click "New document from files (AI).."',
      'Choose one or more files (PDF, Word, text, etc.)',
      'Optionally add context and adjust settings like maximum depth',
      'Click "Generate preview" and review the suggestion before accepting',
    ],
  },
  'suggest-tree-max-depth': {
    simple: 'Maximum structure depth',
    detailed: 'Set the maximum number of levels deep the suggested category structure can be. Higher values create more detailed hierarchies but may be more complex to manage.'
  },
  'suggest-tree-import-mode': {
    simple: 'Import mode',
    detailed:
      'Build category structure creates folders and places content in them. Content only skips folders and clusters file text into content rows in a single content category.',
  },
  'suggest-tree-chars-per-section': {
    simple: 'Characters per section',
    detailed:
      'Target size for each content section. Consecutive short segments are merged until they reach about this many characters, then a new section starts. Default 600.',
  },
  'suggest-tree-show-content': {
    simple: 'Show content text',
    detailed: 'Display a preview of the content that will be included in each category of the suggested category structure. This helps you understand how the AI has organized your content.'
  },
  'suggest-tree-subcase-name': {
    simple: 'Subcase name',
    detailed: 'Enter the subcase name that will be used for imported content. This helps organize content within categories.'
  },
  'suggest-tree-use-suggested-name': {
    simple: 'Use AI-suggested name',
    detailed: 'Use the AI-generated document name instead of a default name. The AI analyzes the content to suggest a meaningful name.'
  },
  'suggest-tree-context': {
    simple: 'Context (optional)',
    detailed:
      'Optional hints for naming and clustering (or segmentation in content-only mode). Examples: domain (“legal contracts”), purpose (“literature review”), structure (“by chapter then topic”), or key themes.',
  },
  'suggest-tree-proceed': {
    simple: 'Generate preview',
    detailed: 'Run AI on the selected files using the context and settings you chose, then review the proposed document structure.'
  },
  'suggest-tree-accept-with-content': {
    simple: 'Content in folders',
    detailed:
      'Default. Create a new document with the category structure and place imported text into the matching folders.',
  },
  'suggest-tree-accept-tree-only': {
    simple: 'Keep content separate',
    detailed:
      'Create the category folders and keep the original imported text in an Original Files Content category under the root (not placed into the leaf folders). Useful when you want the outline first and will place or discard the raw text later.',
  },
  'suggest-tree-accept-categories-only': {
    simple: 'Structure only',
    detailed:
      'Create only the category folders. No content rows are saved — including no Original Files Content stash. Use this when you want the structure as a blank outline.',
  },
  'suggest-tree-custom-name': {
    simple: 'Custom document name',
    detailed: 'Enter a custom name for the document. If left empty, the AI-suggested name will be used. This allows you to provide a more descriptive or meaningful name for your document.'
  },
  'suggest-tree-retry': {
    simple: 'Retry with different settings',
    detailed: 'Generate a new suggested category structure with the current maximum depth setting. This is useful if you want to try different parameters.'
  },
  'suggest-tree-strategy': {
    simple: 'Top-down vs bottom-up',
    detailed:
      'Bottom-up (default) starts with finer content buckets, then merges them upward into parents. Top-down starts with fewer broad categories and subdivides.',
  },
  'expand-tree-context': {
    simple: 'Context (optional)',
    detailed:
      'Optional hints for how new file text relates to the open document. Examples: “continuation of the previous content”, “related but different topics”, “organize by chronological order”, or “focus on technical details”. In content-only mode, hints mainly guide segmentation.',
  },
  'expand-tree-import-mode': {
    simple: 'Import mode',
    detailed:
      'Integrate with category structure matches existing folders and suggests new ones for leftovers. Content only clusters segments into rows with no new folders—you choose an existing content category or a new subcategory under the root (listed first among the root’s children).',
  },
  'expand-tree-chars-per-section': {
    simple: 'Characters per section',
    detailed:
      'Target size for each content section. Consecutive short segments are merged until they reach about this many characters, then a new section starts. Default 600.',
  },
  'expand-tree-max-depth': {
    simple: 'Maximum structure depth',
    detailed:
      'How many levels deep new category folders can go when expanding the open document. Matching content can deepen under empty categories up to this depth; leftovers may attach under empty folders or the root.',
  },
  'expand-tree-strategy': {
    simple: 'Top-down vs bottom-up for new categories',
    detailed: 'When using Add to document from files (AI), content that matches an existing populated category is placed there first. With depth greater than 1, content that matches empty categories builds new subfolders under those empty categories (instead of recreating a parallel top-level structure). Remaining leftovers attach under empty folders when they fit, otherwise under the root. Bottom-up (default) clusters into finer leaf categories and merges upward; top-down builds fewer broad new categories and subdivides.'
  },
  'expand-tree-apply-mode': {
    simple: 'Apply mode',
    detailed:
      'Content in folders (default) places new rows into matching folders. Keep content separate builds the folder outline and stores imported rows in one content category (new “Content of …” or an existing one you pick). Structure only creates folders and does not save content. Optionally create the result as a new document instead of updating the open one.',
  },
  'suggest-parent-structure': {
    simple: 'Structure categories (AI)',
    detailed:
      'On a branch with many subcategories, groups siblings under new parent folders. In the content pane, leave none checked to include all children, or check three or more. Legacy name: Add parent layers (AI).'
  },
  'suggest-parent-structure-disabled': {
    simple: 'Structure categories (AI) (unavailable)',
    detailed: 'See Structure categories (AI) (unavailable).'
  },
  'load-from-file': {
    simple: 'Import .nbr archive',
    detailed:
      'Import a .nbr file from the legacy Narralyzer desktop application. Works from the Document Menu without a selected document.',
    steps: [
      'Open the Document Menu',
      'Click "Import .nbr archive.."',
      'Select a .nbr file from your computer',
      'Confirm the document name if prompted. The document opens in a new tab',
    ],
  },
  'expand-document': {
    simple: 'Add to document from files (AI)',
    detailed:
      'Integrate file text into an open document. Category structure mode matches existing folders and, when depth is greater than 1, builds new subfolders under matching empty categories. Leftover content can still attach under other empty folders or the root. On apply, choose Content in folders (default), Keep content separate (folders plus rows in one content category), or Structure only (empty folders). Content-only mode clusters segments into rows and places them in a content category you choose (or a new subcategory listed first under the root when the document already has folders). In Word (.docx) files, black highlight or a near-black character background is imported as [Redacted], or the equivalent in the document’s main language (for example [מצונזר] in Hebrew).',
    steps: [
      'Select a document in the Document Panel',
      'Open the Document Menu',
      'Click "Add to document from files (AI).."',
      'Choose one or more files (PDF, Word, text, etc.)',
      'Pick import mode: category structure or content only',
      'For content only, choose an existing content category or create a new subcategory listed first under the root',
      'Optionally add context; for category structure, adjust depth and building strategy',
      'Click "Continue", pick apply mode on the preview, then Apply',
    ],
  },
  'expand-document-disabled': {
    simple: 'Cannot add from files',
    detailed: 'Select a document from the list first. Then you can add categories and content from one or more files using AI.'
  },
  
  // Search and navigation
  'find-in-document': {
    simple: 'Find in document',
    detailed:
      'Search names, descriptions, and content in your document. Use Semantic mode for meaning-based search.',
    steps: [
      'Press ⌘/Ctrl+F or click Find in the toolbar',
      'Enter search terms',
      'Choose search in (names, descriptions, content), mode (Any, All, Exact), and options (case sensitive, whole word)',
      'Use Find Next / Find Previous or F3 / ⌘/Ctrl+G to jump between matches',
    ],
  },
  'semantic-search': {
    simple: 'Semantic (Find)',
    detailed:
      'Semantic mode in Find searches by meaning and jumps to matching rows in your document.',
    steps: [
      'Press ⌘/Ctrl+F or click Find in the toolbar',
      'Choose Semantic mode',
      'Enter a phrase or question',
      'Jump to matching rows with Find Next / Find Previous',
    ],
  },
  'ask-ai': {
    simple: 'Ask AI',
    detailed:
      'Chat Q&A about the open document using semantic retrieval over your content.',
    steps: [
      'Open a document tab',
      'Click Ask AI (sparkles icon) in the toolbar',
      'Type a question about your document and send',
      'Review the answer; optional source excerpts and similarity scores in settings',
      'Ask follow-up questions in the same chat',
    ],
  },
  
  // Settings and preferences
  'settings': {
    simple: 'Settings',
    detailed:
      'Open the gear menu on the top bar: appearance (system / light / dark theme), auto-paste, dock-style toolbar icon magnification, toolbar icon size, tooltip font size, short tooltips, detailed help mode, and semantic find / Add matching content similarity threshold. Moving the pointer into the top bar from outside always moves keyboard focus to the first toolbar control when you are not typing in an input, textarea, select, or contentEditable editor (no toggle). Most preferences are stored in the browser; semantic find threshold is saved per user on the server.',
  },
  'two-step-verification': {
    simple: 'Two-step verification',
    detailed:
      'Optional extra sign-in step for username-and-password accounts. Google and Facebook sign-in are unchanged. Open Profile → Two-step verification, enter your password, scan the QR code (or type the key) in any authenticator app, then enter the 6-digit code (it turns on when all six digits are filled). Scanning alone does not finish setup. Save the backup codes, or download them as a text file; each works once if you lose the app. After that, login asks for the code (or a backup code) after your password. Turn it off from the same place with your password and a code.',
    steps: [
      'Open Profile from the user menu',
      'Expand Two-step verification (password accounts only)',
      'Enter your current password and start setup',
      'Scan the QR code in any authenticator app (or enter the key)',
      'Enter the 6-digit code to turn it on, then save or download the backup codes',
    ],
  },
  'settings-theme-system': {
    simple: 'System theme',
    detailed:
      'Match the app appearance to your operating system or browser light/dark mode. It updates when the device theme changes.',
  },
  'settings-theme-light': {
    simple: 'Light theme',
    detailed: 'Always use a light color scheme for the application, regardless of device settings.',
  },
  'settings-theme-dark': {
    simple: 'Dark theme',
    detailed:
      'Always use a dark color scheme for the application, regardless of device settings. Help mode tooltips and the Help dialog use theme-aware colors so text stays readable.',
  },
  'settings-workspace-icon-bar': {
    simple: 'Icon toolbar',
    detailed:
      'Shows the full icon toolbar. When the window is narrow, less-used actions move to the overflow (⋮) menu.',
  },
  'settings-workspace-menu-bar': {
    simple: 'Menu bar',
    detailed:
      'Shows labeled menus (Edit, Category, Content, and more) with a compact quick toolbar for frequent actions.',
  },
  'auto-paste': {
    simple: 'Auto-paste from clipboard',
    detailed:
      'Safari may require Paste or ⌘V in the editor, auto-paste is most reliable in Chrome or Edge.',
    steps: [
      'Open Settings (gear) and enable Auto-paste from clipboard',
      'Select a content category and click Add Content',
      'Clipboard text appears in the editor when the browser allows access',
      'Chrome/Edge: allow clipboard access once if prompted',
      'Safari: use Paste or ⌘V if content does not appear automatically',
    ],
  },
  'toolbar-dock-magnify': {
    simple: 'Dock-style toolbar icons',
    detailed:
      'Magnify toolbar icons as the pointer moves, similar to the macOS Dock: the nearest icon grows and neighbors scale slightly for easier targeting.',
  },
  'show-content-index-in-pane': {
    simple: 'Show content row numbers',
    detailed:
      'When enabled, the content pane table shows a No. Column with each row\'s position in its category (1, 2, 3…). Numbers are for display only; the server still stores 0-based indices internally.',
  },
  'click-to-rename-category-in-tree': {
    simple: 'Click category name to rename',
    detailed:
      'When enabled, clicking the name of an already-selected category in the category structure starts inline rename (like macOS Finder). When off, use Rename in the toolbar or context menu only, helps avoid accidental rename mode.',
  },
  'tooltip-font-size': {
    simple: 'Tooltip font size',
    detailed: 'Adjust the size of tooltip text to improve readability. Choose from small, medium, or large font sizes.'
  },
  'toolbar-icon-size': {
    simple: 'Toolbar icon size',
    detailed:
      'Change how large the main toolbar icons appear (menu, actions, help, and settings). Larger icons are easier to tap; smaller icons leave more room for narrow windows.',
  },
  'settings-find-similarity-threshold': {
    simple: 'Semantic similarity threshold',
    detailed:
      'How similar a result must be to count as a match for Find (Semantic) and Add matching content (AI). Lower values return more results; higher values keep only close matches. Saved per user on the server.',
  },
  'settings-find-show-similarity-score': {
    simple: 'Show Find similarity scores',
    detailed:
      'Toggle in Find (Semantic mode) → Search options → Show similarity scores. Preference is saved per user.',
  },
  'settings-hybrid-semantic-ranking': {
    simple: 'Prefer exact words (hybrid ranking)',
    detailed:
      'When on, Find (Semantic) and Ask AI rank rows that contain your query words above paraphrase-only hits. Similarity scores and the threshold still use pure semantic similarity (not a boosted percent). Does not apply to Add matching content (AI), which matches on category meaning only. Saved per user.',
  },
  'find-hybrid-semantic-ranking': {
    simple: 'Prefer exact words',
    detailed:
      'Ranks Find results that contain the words you typed above paraphrase-only matches. Does not change similarity scores or the threshold. Same preference as Settings → Prefer exact words (hybrid ranking).',
  },
  'semantic-hybrid-ranking': {
    simple: 'Prefer exact words (hybrid ranking)',
    detailed:
      'When retrieving context for Ask AI, prefer passages that contain your question words for ranking, while similarity scores stay pure semantic. Saved per user; also used by Find (Semantic). Does not apply to Add matching content.',
  },
  'settings-add-matching-show-similarity-score': {
    simple: 'Show Add matching content scores',
    detailed:
      'Toggle in the Add matching content (AI) dialog → Show similarity scores on matched rows. Preference is saved per user.',
  },
  'add-matching-show-similarity-score': {
    simple: 'Show similarity scores',
    detailed:
      'When enabled, Add matching content (AI) shows a similarity percentage on each matched row during step-by-step and quick review.',
  },
  'help-mode': {
    simple: 'Detailed help tooltips',
    detailed:
      'When enabled, toolbar and control tooltips show longer explanations (simple line plus details). In dark theme, help tooltips use a dark amber panel and light text so they stay readable. Pair with Short tooltips for menu rows, or turn Short tooltips off when you only want full help on controls.',
  },
  'short-tooltips': {
    simple: 'Short tooltips',
    detailed: 'Show brief tooltips for all UI elements, including text-based menu items. When disabled, only detailed help tooltips are shown when help mode is enabled.'
  },
  
  // General UI
  'undo': {
    simple: 'Undo',
    detailed:
      'Undoing a content delete restores text and attachments while the delete remains in the undo history.',
    steps: [
      'Click Undo in the toolbar',
      'Or press ⌘/Ctrl+Z when focus is outside text fields and the editor',
      'Repeat to step back through multiple actions',
    ],
  },
  'undo-disabled': {
    simple: 'Cannot undo',
    detailed: 'No actions are available to undo. Perform some actions first to enable the undo feature.'
  },
  'redo': {
    simple: 'Redo',
    detailed:
      'Redoing a content delete removes the row again and clears the editor if that content was open.',
    steps: [
      'Click Redo in the toolbar',
      'Or press ⌘/Ctrl+Shift+Z when focus is outside text fields and the editor',
    ],
  },
  'redo-disabled': {
    simple: 'Cannot redo',
    detailed: 'No actions are available to redo. Undo some actions first to enable the redo feature.'
  },
  'expand-all': {
    simple: 'Expand all',
    detailed: 'Open all collapsed categories to show the complete document structure at once.'
  },
  'collapse-all': {
    simple: 'Collapse all',
    detailed: 'Close all expanded categories to show only the top-level structure for easier navigation.'
  },
  'category-tree-expand': {
    simple: 'Expand or collapse in the category structure',
    detailed:
      'Click the chevron or double-click a category label to open or close one level. ⌘/Ctrl+double-click a branch label expands or collapses the entire branch under that category.',
  },
  'show-sub-tree': {
    simple: 'Category Structure Overview',
    detailed:
      'Shows every category in scope with its content at once in an overlay view. Hover a content row for actions: adjust text (plain-text rows with a same-subcase neighbour), copy or move to another category, or delete. Copy keeps the original; move and delete can be reverted with Undo.',
    steps: [
      'Select a category (document root to see the entire document)',
      'Click Category Structure Overview in the toolbar or context menu',
      'Choose Textual for list-style columns or Graphical for a diagram (Horizontal or Vertical orientation)',
      'Hover a content row: adjust text, copy/move to another category, or delete',
      'Optionally use Filters to narrow by subcase; Print or Export DOCX / Export PNG from the overlay',
    ],
  },
  'show-sub-tree-disabled': {
    simple: 'Category Structure Overview (unavailable)',
    detailed: 'Select a category in the category structure (the root of the scope to view). Open a document tab first.'
  },
  'duplicate-as-document': {
    simple: 'Duplicate as document',
    detailed:
      'Creates a new library document from a selected branch with chosen categories and subcases. Each included folder needs at least two child categories selected.',
    steps: [
      'Select a category in the category structure (root of the branch you want)',
      'Click Duplicate as document in the toolbar or context menu',
      'Enter a name, check which categories from the branch to include, and choose subcases (groups or individual)',
      'Confirm. A new document is created in your library with that structure and content',
    ],
  },
  'duplicate-as-document-disabled': {
    simple: 'Duplicate as document (unavailable)',
    detailed: 'Select a category in the category structure to create a new document from that branch. This option is disabled if no category is selected.'
  },
  'content-category-to-branch-options': {
    simple: 'Ways to turn a content category into a branch',
    detailed:
      'A content category holds text rows; a branch folder holds subcategories. Any method that adds subcategories under a content category converts it to a branch. Choose based on whether you want a manual split, AI clustering, or pasted structure.',
    steps: [
      'Split category — select the content category, optionally select rows for the first child, then Split category in the toolbar or context menu and name two subcategories',
      'Structure categories (AI) — select a content category with at least two rows, open Structure categories (AI), choose strategy and depth, preview, then apply',
      'Structure builder, Instructions (AI) — with a document tab open, describe splitting the category or adding subfolders; uses your current row selection when non-empty',
      'Structure builder, Import tab — select the content category, paste two or more top-level folder lines (indent or bullets), Build proposal from paste, then Apply',
      'Cut, copy, and paste categories — duplicate or move folder structure onto a branch; if the target is still a content category, use Split category or Structure categories (AI) first',
      'Drag and drop categories — after the parent is a branch, reorder or nest subcategories; hold Alt (Option on Mac) while dropping to duplicate where supported',
    ],
    stepHelpKeys: [
      'split-category',
      'structure-categories',
      'tree-assistant-input',
      'tree-assistant-outline',
      'cut-copy-paste-categories',
      'drag-drop-categories',
    ],
  },
  'split-category': {
    simple: 'Split category',
    detailed:
      'Turns a content category into a branch with two named children. Without a row selection, existing content moves into a temporary child (Temp …). With rows selected first, selected rows go to the first child and the rest to the second.',
    steps: [
      'Select a content category (one that holds text rows)',
      'Optional: in the content pane, select the rows that should go to the first new child',
      'Click Split category in the toolbar or context menu',
      'Enter names for the two subcategories and confirm',
    ],
  },
  'split-category-disabled': {
    simple: 'Cannot split category',
    detailed: 'Split into two subcategories applies only to a content category (one that holds rows, with no subfolders yet). Select that category first.'
  },
  'group-categories': {
    simple: 'Group categories',
    detailed:
      'Creates a new parent folder and moves selected sibling categories under it. Unselected siblings stay in place.',
    steps: [
      'Select the parent branch in the category structure',
      'In the content pane, multi-select two or more direct children, leaving at least one sibling unselected',
      'Click Group categories in the toolbar or context menu',
      'Enter a name for the new group and confirm',
    ],
  },
  'group-categories-disabled': {
    simple: 'Group categories (unavailable)',
    detailed:
      'Select a branch in the category structure, check at least two direct children in the content pane, and leave at least one sibling unselected.'
  },
  'flatten-category': {
    simple: 'Flatten category',
    detailed:
      'Promotes a branch\'s subcategories up one level and removes the selected category. Nested structure under promoted children stays unchanged.',
    steps: [
      'Select a non-root category that has subcategories',
      'Click Flatten category in the toolbar or context menu',
      'Its subcategories move up to replace it; the selected category is removed',
    ],
  },
  'flatten-category-disabled': {
    simple: 'Flatten category (unavailable)',
    detailed:
      'Flatten needs a non-root branch with subcategories, and lifted child names must not duplicate the grandparent or any existing sibling (letter case ignored). Rename conflicts first.'
  },
  'collapse-subtree-to-content-category': {
    simple: 'Collect branch content',
    detailed:
      'Gathers every row from the branch into the selected category and removes subcategory folders, turning it into a content category. Duplicate rows (same subcase and text) are merged into one. Select two or more branches to collect each of them in a single action.',
    steps: [
      'Select a non-root branch that has subcategories (or check several branches in the category structure)',
      'Click Collect branch content in the toolbar or context menu',
      'All rows from each branch move into it; subfolders are removed and it becomes a content category',
    ],
  },
  'collapse-subtree-to-content-category-disabled': {
    simple: 'Collect branch content (unavailable)',
    detailed:
      'Select a non-root branch that has subcategory folders.'
  },
  'add-matching-content': {
    simple: 'Add matching content (AI)',
    detailed:
      'Copies or moves semantically matching rows from another category on the path to the root. Uses the similarity threshold from Settings (gear) → Semantic find & matching. Optionally show per-row similarity scores in this dialog. Skips rows already in the target with the same subcase and text. Folders in the picker are for navigation only—select a content category as the source. In step-by-step review, keyboard shortcuts speed things up: ← Back, → Next, A Accept, S Skip. In step-by-step review (copy or move), use “Adjust text…” to borrow text into a row when an adjacent reviewed row shares its subcase: click a spot in the previous reviewed row to mark text up to its end (prepended) or in the next reviewed row from its start (appended). A side is offered only when that neighbour has the same subcase. A “[...]” line is inserted after the prepended passage and before the appended one to mark each borrowed boundary. Save updates that row’s text immediately; the previous and next rows are left untouched. The edit can be reverted in the dialog or with Undo afterwards.',
    steps: [
      'Select the destination content category',
      'Click Add matching content (AI) in the toolbar or context menu',
      'Choose a source under a folder on the path to the root',
      'Pick copy or move, apply mode (all, step-by-step, or quick review), then confirm',
      'Optional (step-by-step, copy or move): on a row whose previous or next reviewed row has the same subcase, click Adjust text…, mark text in that neighbour, Prepend/Append it, then Save to update this row’s text',
    ],
  },
  'add-matching-apply-mode': {
    simple: 'How to apply',
    detailed:
      'Apply all matches copies or moves every match immediately. Step-by-step review lets you Accept or Skip each row (← → navigate; A Accept; S Skip)—nothing is written until Apply. Quick review shows a checkbox list so you can select which matches to apply.',
  },
  'add-matching-content-disabled': {
    simple: 'Add matching content (AI) (unavailable)',
    detailed:
      'Add matching rows from another category (AI) needs a selected content category and at least one other content category with rows on the path from that category up to the document root. Semantic find must be enabled.'
  },
  'compare-content': {
    simple: 'Compare content and select',
    detailed:
      'Compares rows in the open category against another category or branch and selects matched or unmatched rows. Useful for cleaning up temp categories after Add matching content (copy).',
    steps: [
      'Open a content category that has at least one row',
      'Click Compare content and select in the toolbar or context menu',
      'In the category structure, pick a content category or a folder (branch scope)',
      'Choose match rule: same subcase and text, or same subcase only',
      'Review matched/unmatched counts, then Select matched or Select unmatched',
      'Optional: check Split into two subcategories — the button you click puts those rows in the first child and the rest in the second (name both in the next dialog)',
    ],
  },
  'compare-content-disabled': {
    simple: 'Compare content and select (unavailable)',
    detailed:
      'Needs a content category with at least one row and a compare target (another content category with rows or a branch that contains content) along the path to the document root. Enable the Compare content and select feature flag.'
  },
  'merge-categories': {
    simple: 'Merge categories',
    detailed:
      'Combines selected content categories into one content category. Skips duplicate subcase+text rows.',
    steps: [
      'Select the parent branch in the category structure',
      'In the content pane, multi-select two or more content categories, leaving at least one sibling unselected',
      'Click Merge categories in the toolbar or context menu',
      'Enter a name for the new content category and confirm',
    ],
  },
  'merge-categories-disabled': {
    simple: 'Merge categories (unavailable)',
    detailed:
      'Select a branch in the category structure, check at least two content categories in the content pane, and leave at least one sibling unselected.'
  },
  'move-up': {
    simple: 'Move up',
    detailed: 'Move the selected item up in the list. This changes the order within the same parent category.'
  },
  'move-up-disabled': {
    simple: 'Cannot move up',
    detailed: 'The selected item is already at the top of its list or no items are selected.'
  },
  'move-down': {
    simple: 'Move down',
    detailed: 'Move the selected item down in the list. This changes the order within the same parent category.'
  },
  'move-down-disabled': {
    simple: 'Cannot move down',
    detailed: 'The selected item is already at the bottom of its list or no items are selected.'
  },
  'cut': {
    simple: 'Cut',
    detailed: 'Remove the selected item from its current location and place it in your clipboard for pasting elsewhere.'
  },
  'cut-disabled': {
    simple: 'Cannot cut',
    detailed: 'No items are currently selected. Select one or more items to cut them.'
  },
  'copy': {
    simple: 'Copy',
    detailed: 'Create a duplicate of the selected item and place it in your clipboard for pasting elsewhere.'
  },
  'copy-disabled': {
    simple: 'Cannot copy',
    detailed: 'No items are currently selected. Select one or more items to copy them.'
  },
  'paste': {
    simple: 'Paste',
    detailed: 'Insert a previously copied or cut item at this location.'
  },
  'paste-disabled': {
    simple: 'Cannot paste',
    detailed: 'No item is currently in your clipboard. Copy or cut an item first, then paste it here.'
  },
  'rename-subcase': {
    simple: 'Rename subcase',
    detailed:
      'Renames a subcase label everywhere in the document, updating every row and group membership. The new name must be unique.',
    steps: [
      'Open a document tab',
      'Click Rename subcase in the toolbar (or More menu)',
      'Select the subcase to rename and enter the new name',
      'Confirm. All content rows and group memberships using that label are updated',
    ],
  },
  'rename-subcase-disabled': {
    simple: 'Cannot rename subcase',
    detailed: 'No subcase is selected for renaming. Select a subcase from the list first.'
  },
  'manage-subcase-groups': {
    simple: 'Manage subcase groups',
    detailed:
      'Subcase groups hold multiple subcases so you can select them together in Find, Category Structure Overview, Filter content by subcase, and Duplicate as document. A subcase can belong to multiple groups.',
    steps: [
      'Open a document tab',
      'Click Manage subcase groups in the toolbar (or More menu)',
      'Add a group name, select the group, and check subcases to include',
      'Save or Done. Groups are available across the app',
    ],
  },
  'manage-subcase-groups-disabled': {
    simple: 'Cannot manage subcase groups',
    detailed: 'No subcase groups are available to manage. Create some subcase groups first.'
  },
  'select-subcase': {
    simple: 'Select subcase',
    detailed:
      'Choose which subcase to work with in the content panel header (editing focus). To limit which subcases appear as rows in the content table, use the toolbar filter icon (filter list) next to Find.'
  },
  'select-subcase-disabled': {
    simple: 'Cannot select subcase',
    detailed: 'No subcases are available to select. Add some subcases to your document first.'
  },
  'content-pane-subcase-filter': {
    simple: 'Filter content by subcase',
    detailed:
      'Limits which rows appear in the content table while the category structure stays fully visible. Hidden rows remain in the document.',
    steps: [
      'Open a document with multiple subcases',
      'Click Filter content by subcase (filter-list icon in the toolbar, near Find)',
      'Choose subcase groups or individual subcases, the content pane updates live',
      'Selecting every subcase clears the filter; Cancel restores the filter from when you opened the dialog',
    ],
  },
  'content-pane-subcase-filter-disabled': {
    simple: 'Filter content by subcase (unavailable)',
    detailed: 'Open a document tab that has subcases first.'
  },
  'content-pane-subcase-filter-active': {
    simple: 'Subcase filter is on',
    detailed:
      'Shows only rows for the selected subcases; the category structure is unchanged. Click to change or clear. Hidden rows remain in the document. The icon pulses gently while active (disabled under reduced-motion).'
  },

  // Content editor
  'content-subcase': {
    simple: 'Subcase',
    detailed: 'Select or enter the subcase name for this content item. Subcases help organize content by topic or section.'
  },
  'content-language': {
    simple: 'Language',
    detailed: 'Choose the language for speech recognition. This affects how the system interprets spoken words.'
  },
  'speech-recording': {
    simple: 'Start recording',
    detailed: 'Begin voice recording to add text to your content. Speak clearly and the text will appear automatically.'
  },
  'speech-recording-stop': {
    simple: 'Stop recording',
    detailed: 'Stop voice recording. The recorded text will be added to your content.'
  },
  'content-save': {
    simple: 'Save content',
    detailed: 'Save your changes to this content item. The content will be stored and available for future editing.'
  },
  'content-cancel': {
    simple: 'Cancel editing',
    detailed: 'Discard your changes and return to viewing mode. Any unsaved changes will be lost.'
  },
  'content-editor': {
    simple: 'Content editor',
    detailed:
      'Edit the text content of this item. Use formatting, links, and images in the body. Video and audio (and optionally images) can be attached via the toolbar and opened from Media instead of embedding in the text.'
  },
  'content-text-area': {
    simple: 'Content text area',
    detailed:
      'Edit the main rich text. Images can be embedded here, or attached only through the Media list when Embed image is off. Video and audio are always listed under Media with Play or View.'
  },
  'content-insert-image': {
    simple: 'Insert image',
    detailed:
      'Choose an image file (max 100 MB). When Embed image is on (default), the image is placed in the document. When Embed image is off, the file is uploaded and listed under Media (like video/audio) so you open it with View without filling the body.'
  },
  'content-insert-image-disabled': {
    simple: 'Insert image',
    detailed: 'Switch to edit mode to add images.'
  },
  'content-embed-image': {
    simple: 'Embed image',
    detailed:
      'Toggle how new image uploads behave. On: images go into the document body. Off: images are attached to the Media panel only. The choice is remembered on this browser.'
  },
  'content-embed-image-disabled': {
    simple: 'Embed image',
    detailed: 'Switch to edit mode to change this setting.'
  },
  'content-attached-media': {
    simple: 'Attached media',
    detailed:
      'Lists video, audio, and externally attached images for this content (each file max 100 MB). Use View or Play to open in a dialog. While editing, you can remove an attachment with the trash icon (files on disk are removed; Undo delete of the whole content can still restore if files were not purged yet).'
  },
  'content-large-preview': {
    simple: 'Content preview',
    detailed:
      'Open the current content in a full-screen read-only viewer with more room for text and images. Shown next to Edit when a single content row is selected. Uses the latest text in the editor while you are editing. Close the viewer to return.'
  },
  'content-expand-editor': {
    simple: 'Expand editor',
    detailed:
      'Open the rich text editor in a full-screen window so you can edit with more space. Your text stays in sync with the panel. Close the window or save to return.'
  },

  // Find dialog
  'find-search': {
    simple: 'Search',
    detailed: 'Search for text in categories, descriptions, and content. Use the options below to customize your search.'
  },
  'find-next': {
    simple: 'Find next',
    detailed:
      'Go to the next match (toolbar button). Shortcut when the Find dialog is focused outside search fields: F3 or ⌘/Ctrl+G (⌘ = Mac Cmd).',
  },
  'find-match-navigation': {
    simple: 'Navigation between matches',
    detailed:
      'Shortcuts apply when the Find dialog is focused outside search fields.',
    steps: [
      'Run a search in the Find dialog',
      'Click Find Next or Find Previous in the dialog toolbar',
      'Shortcuts: F3 or ⌘/Ctrl+G for next; Shift+F3 or ⌘/Ctrl+Shift+G for previous',
      'Use Next category / Previous category to jump between categories that contain matches',
    ],
  },
  'find-previous': {
    simple: 'Find previous',
    detailed:
      'Go to the previous match. Shortcut when the Find dialog is focused outside search fields: Shift+F3 or ⌘/Ctrl+Shift+G.'
  },
  'find-next-category': {
    simple: 'Next category',
    detailed: 'Navigate to the next category that contains search matches. This skips to the next category regardless of how many content matches are in the current category.'
  },
  'find-previous-category': {
    simple: 'Previous category',
    detailed: 'Navigate to the previous category that contains search matches. This skips to the previous category regardless of how many content matches are in the current category.'
  },
  'find-search-names': {
    simple: 'Search in names',
    detailed: 'Include category and content names in your search. This is usually the most useful option.'
  },
  'find-search-descriptions': {
    simple: 'Search in descriptions',
    detailed: 'Include category descriptions in your search. Useful for finding categories by their purpose.'
  },
  'find-search-content': {
    simple: 'Search in content',
    detailed: 'Include the actual text content in your search. This searches through all content items.'
  },
  'find-search-mode': {
    simple: 'Search mode',
    detailed: 'Choose how to match your search terms: Any (at least one term), All (all terms), or Exact (exact phrase).'
  },
  'find-whole-word': {
    simple: 'Whole word',
    detailed: 'Matches whole words only. For example, searching for "cat" matches the word cat alone.'
  },
  'find-case-sensitive': {
    simple: 'Case sensitive',
    detailed: 'Match the exact case of letters. For example, "Cat" and "cat" will be treated as different.'
  },
  'find-subcase-filter': {
    simple: 'Filter by subcases',
    detailed:
      'Limit your search to specific subcases. When enabled, all subcases start selected (same as Category Structure Overview); narrow with groups or individual checkboxes.'
  },
  'find-subcase-groups': {
    simple: 'Subcase groups',
    detailed: 'Select groups of subcases to include in your search. This is a quick way to filter by multiple related subcases.'
  },
  'find-subcase-individual': {
    simple: 'Individual subcases',
    detailed: 'Select specific subcases to include in your search. You can choose multiple subcases.'
  },
  'find-search-input': {
    simple: 'Search text',
    detailed: 'Enter the text you want to search for. The placeholder text changes based on your selected search mode.'
  },
  'find-search-options': {
    simple: 'Search options',
    detailed: 'Applies to text search modes in the Find dialog (Any, All, Exact). Semantic mode uses the similarity threshold in Settings (gear) → Semantic find & matching.',
    steps: [
      'Open Find (⌘/Ctrl+F)',
      'Expand Search options if collapsed',
      'Toggle search in: names, descriptions, content',
      'Set mode: Any, All, or Exact phrase',
      'Enable case sensitive or whole word only as needed',
      'Optionally filter by subcase groups or individuals',
    ],
  },
  'find-search-options-toggle': {
    simple: 'Toggle search options',
    detailed: 'Show or hide the search options panel to configure your search settings.'
  },
  'find-semantic-settings': {
    simple: 'Semantic (Find) settings',
    detailed:
      'When Find mode is Semantic, use Show similarity scores in Search options. Similarity threshold is in Settings (gear) → Semantic find & matching.',
  },
  'find-similarity-threshold': {
    simple: 'Threshold',
    detailed:
      'Adjust how similar results must be to your search query. Configure in Settings (gear) → Semantic find & matching. Lower values show more results; higher values show only very similar results.',
  },
  'find-show-similarity-score': {
    simple: 'Show scores',
    detailed:
      'Display similarity percentages for semantic search results. Available when Find mode is Semantic (Search options).',
  },
  'find-subcase-selection': {
    simple: 'Subcase selection',
    detailed: 'Choose which subcases to include in your search. You can select groups or individual subcases.'
  },
  'find-subcase-selection-toggle': {
    simple: 'Toggle subcase selection',
    detailed: 'Show or hide the subcase selection panel to filter your search by specific subcases.'
  },
  'find-subcase-select-all': {
    simple: 'Select all subcases',
    detailed:
      'Select every document subcase for the active filter. Clearing this clears all subcases (no matches) until you select again, same behavior as Category Structure Overview.'
  },
  'find-subcase-group': {
    simple: 'Subcase group',
    detailed:
      'Same as Category Structure Overview: with all subcases selected, the first group narrows to that group’s subcases; additional groups add their subcases. Turning off the last group restores your selection from before you used groups.'
  },
  'find-subcase-individual-item': {
    simple: 'Individual subcase',
    detailed: 'Select a specific subcase to include in your search. You can select multiple individual subcases.'
  },
  'find-close': {
    simple: 'Close find dialog',
    detailed: 'Close the find dialog and return to normal document view.'
  },
  'find-help': {
    simple: 'Find dialog help',
    detailed:
      'Open Find from the document with ⌘/Ctrl+F when focus is not in a normal input or textarea. With Find open (focus outside its search fields): Enter runs search; F3 or ⌘/Ctrl+G finds next; Shift+F3 or ⌘/Ctrl+Shift+G finds previous; Escape closes the dialog.'
  },
  
  // Category Structure Overview overlay (subcase / group filter at top of view)
  'show-subtree-subcases': {
    simple: 'Filter subcases',
    detailed:
      'In Category Structure Overview, use the list at the top to include or exclude individual subcases. The view updates as you change the selection.',
  },
  'show-subtree-select-all': {
    simple: 'Select all subcases',
    detailed:
      'In Category Structure Overview, include every document subcase again. Clearing all subcases hides content until you select at least one.',
  },
  'show-subtree-show': {
    simple: 'Category Structure Overview',
    detailed:
      'Opens Category Structure Overview for the selected category and descendants (document root = entire document). All categories in scope show content at once. All subcases included by default; narrow with group chips or the subcase list. Hover a content row for adjust text, copy/move to another category, or delete — without leaving the overview. Print or Export DOCX from the overlay.',
  },
  'subtree-layout-vertical': {
    simple: 'Textual view',
    detailed:
      'Textual view: stack the branch in vertical columns, list-style layout with optional split compare between subcase sets.',
  },
  'subtree-layout-horizontal': {
    simple: 'Graphical view',
    detailed:
      'Graphical view: draw the branch as a diagram. Horizontal lays levels top-to-bottom with siblings side by side; Vertical flows left-to-right (or right-to-left for Hebrew roots) with siblings stacked.',
  },
  'subtree-graphical-horizontal': {
    simple: 'Horizontal diagram',
    detailed:
      'Top-down graphical layout: the root is centered at the top and each generation spreads horizontally below the previous one.',
  },
  'subtree-graphical-vertical': {
    simple: 'Vertical diagram',
    detailed:
      'Side-flow graphical layout: the root anchors the reading edge (left for LTR names, right for Hebrew/RTL roots) and each generation extends across the diagram with siblings stacked vertically.',
  },
  'subtree-graphical-content-count': {
    simple: 'Content count',
    detailed:
      'Show how many content rows each node has. Content categories show their direct row count; branch categories show the total aggregated across the branch below them.',
  },
  'subtree-graphical-node-width': {
    simple: 'Category box width',
    detailed:
      'Drag to make category boxes wider or narrower in the graphical diagram (down to 72px). Layout spacing adjusts automatically; your choice is remembered for the next overview.',
  },

  'tree-assistant': {
    simple: 'Structure builder',
    detailed:
      'Build or change the category structure (toolbar handyman icon). Two tabs: Instructions (AI) for natural-language proposals you review and Apply (undo/redo records batches). Import for pasted folder outlines and/or subcase content blocks in one editor, with optional Load file (.txt/.md). With a document tab open the model sees your category structure; with no tab you can create a new document or duplicate one from your library via Instructions (AI). Optional voice input on Instructions (AI).',
    steps: [
      'Click Structure builder (handyman icon) in the toolbar',
      'Instructions (AI): describe category structure changes, review the proposal, then Apply',
      'Import: paste folders (indent) and/or Subcase: blocks, Build proposal from paste, then Apply',
    ],
  },
  'tree-assistant-disabled': {
    simple: 'Structure builder (unavailable)',
    detailed:
      'If the toolbar icon is missing, Structure builder may be turned off for this deployment. When enabled, use it without a document tab to create or duplicate a document from the library, or with a tab for full category-structure proposals.',
  },
  'tree-assistant-input': {
    simple: 'Instructions (AI) tab',
    detailed:
      'On the Instructions (AI) tab, type what you want done: new document, duplicate from library, delete document by name, add/move/rename/delete categories, split a content category, copy a branch, organize content, etc. Without a document tab you can create or duplicate a document; with a tab open the model sees your category structure. Chat history is kept per document for this session.\n\n' +
      'Click any of your prior messages to reload that prompt into the box, edit if needed, then send. After typing document, category (cat…), rename category, or a partial name, autocomplete suggests library document names or category folder names from the category structure (↑/↓ Enter Tab to pick). The full id appears in smaller text under each row. Matching uses that category\'s own name, not parent names or the dotted id path; type : to narrow by internal id fragments. Duplicate names show as Name (Parent). Esc closes suggestions.\n\n' +
      'Duplicate-document proposals may open a picker for subcases and groups when the source is in your library. Review proposals in summary or JSON; organize shows a folder preview before apply. Split uses your current content selection when non-empty (same as the toolbar). Use the Import tab for pasted folder or subcase lists instead of the model.',
  },
  'tree-assistant-send': {
    simple: 'Send',
    detailed:
      'Submit your Instructions (AI) message. The server replies with text and, when applicable, a structured proposal to review before Apply.',
  },
  'tree-assistant-voice': {
    simple: 'Voice prompt',
    detailed:
      'Dictate into the message field using the browser speech API (Chrome, Edge, Safari with WebKit). Click again to stop. Grant microphone permission if prompted. Set recognition language in the compact dropdown below the microphone (full language names in the list), not from the rich-text editor.',
  },
  'tree-assistant-voice-language': {
    simple: 'Voice language',
    detailed:
      'Locale for speech-to-text in Structure builder (below the microphone). The closed control shows a short code (e.g. EN, HE); the menu lists full language names. Stored in the browser as treeAssistantSpeechLanguage.',
  },
  'tree-assistant-outline': {
    simple: 'Import tab',
    detailed:
      'Paste or load a .txt/.md file with folder structure (indent or dash bullets) and/or subcase blocks in one proposal. A line ending with a colon alone (Subcase:) starts a content block; following non-empty lines become rows until the next header.\n\n' +
      'With a document tab: folders attach under the category selected in the category structure. Content-only paste (subcase blocks without new folders) requires a selected content category. With no document tab, paste creates a new document and category structure; use Subcase: under folder lines for content rows.\n\n' +
      'Optional Create empty document first attaches the paste to a new document root when a tab is open. Editor toolbar: indent/outdent (2 spaces per level; Tab / Shift+Tab), toggle dash bullets, new line at same or deeper level, Example, Load file. Click Build proposal from paste, review, then Apply.',
    steps: [
      'Open Structure builder and switch to the Import tab',
      'Paste or Load file (.txt/.md) with folders and/or Subcase: blocks',
      'Click Build proposal from paste, review the proposal, then Apply',
    ],
  },
  'tree-assistant-bulk-rows': {
    simple: 'Import tab (subcase blocks)',
    detailed:
      'Subcase content lists are pasted on the Import tab (not a separate tab). Use a line ending with a colon alone (Witness:) then one row per line. For content-only import, select a content category in the category structure first. Combine with indented folder lines in the same paste when you need both structure and rows.',
  },

  // Ask AI panel
  'ask-ai-prompt': {
    simple: 'Ask AI',
    detailed:
      'Open the Ask AI chat panel to ask questions about the open document in plain language. Answers use semantic retrieval over your content. Requires an open document tab.'
  },
  'ask-ai-send': {
    simple: 'Send',
    detailed: 'Send your question to Ask AI. The model analyzes relevant content from your document and replies in the chat.'
  },
  'ask-ai-clear': {
    simple: 'Clear conversation',
    detailed: 'Clear the Ask AI chat history and start a fresh conversation about the current document.'
  },
  'ask-ai-export': {
    simple: 'Export conversation',
    detailed: 'Save the current Ask AI conversation to a file for future reference or sharing.'
  },
  'semantic-search-query': {
    simple: 'Question',
    detailed: 'Type your question about the document. Ask AI finds relevant passages and composes an answer; follow up in the same chat.'
  },
  'semantic-clear-history': {
    simple: 'Clear conversation',
    detailed: 'Delete all messages in this Ask AI chat and start over. History is per document when auto-clear is enabled.'
  },
  'semantic-settings': {
    simple: 'Ask AI settings',
    detailed:
      'Configure Ask AI: similarity threshold, max content snippets sent to the model, auto-clear when switching documents, and whether to show source excerpts and scores. Voice recognition language is set below the microphone in the question field.'
  },
  'semantic-close': {
    simple: 'Close Ask AI',
    detailed: 'Close the Ask AI panel and return to the main editor. Conversation history is kept for this document unless you cleared it.'
  },
  'semantic-send': {
    simple: 'Send',
    detailed: 'Send your question to Ask AI. The AI uses semantically similar content from your document to build its answer.'
  },
  'semantic-voice': {
    simple: 'Voice question',
    detailed:
      'Speak your question; text appears in the field while you talk. Click again to stop. Uses the browser speech engine (Chrome, Edge, Safari with webkit). Set recognition language in the compact dropdown below the microphone (full language names in the list). The microphone needs permission.',
  },
  'semantic-voice-language': {
    simple: 'Voice language',
    detailed:
      'Locale for speech-to-text in Ask AI (below the microphone). The closed control shows a short code (e.g. EN, HE); the menu lists full language names. Saved in the browser (same key as Structure builder voice).',
  },
  'semantic-similarity-threshold': {
    simple: 'Threshold',
    detailed: 'Adjust how similar content must be to your question for the AI to include it. Lower values (0.1-0.3) include more content, higher values (0.7-0.9) include only very relevant content.'
  },
  'semantic-max-results': {
    simple: 'Max results',
    detailed: 'Set the maximum number of content items to send to the AI for context. More items provide more context but may slow down responses. Recommended: 3-5 for focused answers, 7-10 for comprehensive responses.'
  },
  'semantic-auto-clear-history': {
    simple: 'Auto clear',
    detailed: 'Automatically clear conversation history when switching documents. This helps keep conversations focused on the current document and prevents confusion between different documents.'
  },
  'semantic-show-sources': {
    simple: 'Show sources',
    detailed: 'Display the source content that the AI used to generate its response. This helps you understand where the information came from and verify the accuracy of the AI\'s answer.'
  },
  'semantic-show-similarity-score': {
    simple: 'Show scores',
    detailed: 'Display similarity percentages for source texts to show how relevant each piece of content was to your question. Higher percentages indicate more relevant content.'
  },
  'semantic-settings-close': {
    simple: 'Close settings',
    detailed: 'Close the settings panel and return to the AI conversation view. Your settings will be saved automatically.'
  },
  'semantic-question-input': {
    simple: 'Ask a question',
    detailed:
      'Enter a question about your document in plain language. Ask AI retrieves relevant rows and replies in the chat. Enter to send; Shift+Enter for a new line.'
  },
  
  // Duplicate category as document dialog
  'duplicate-category-document-name': {
    simple: 'New document name',
    detailed: 'Name for the new document created from the selected branch.'
  },
  'duplicate-category-select-categories': {
    simple: 'Select categories',
    detailed:
      'Check which categories from the branch to include in the new document. For each folder you include, at least two of its child categories must also be selected.'
  },
  'duplicate-category-category-tree': {
    simple: 'Category structure',
    detailed: 'Branch under your selection, check categories to copy into the new document.'
  },
  'duplicate-category-select-subcases': {
    simple: 'Select subcases',
    detailed: 'Choose which subcases to include, only content rows with those subcases are copied. Use groups or individual checkboxes.'
  },
  'duplicate-category-subcase-selection-toggle': {
    simple: 'Toggle subcase selection',
    detailed: 'Show or hide the subcase selection panel to choose which subcases to include.'
  },
  'duplicate-category-subcase-groups': {
    simple: 'Subcase groups',
    detailed: 'Toggle groups of related subcases to include in the new document. Multiple groups can be selected; their subcases are merged with any individual selections. Click again to remove a group from the filter.'
  },
  'duplicate-category-subcase-individual': {
    simple: 'Individual subcases',
    detailed: 'Select specific subcases to include in the new document. You can choose multiple individual subcases.'
  },
  'duplicate-category-subcase-select-all': {
    simple: 'Select all subcases',
    detailed: 'Include all available subcases in the new document. This removes subcase filtering.'
  },
  'duplicate-category-subcase-individual-item': {
    simple: 'Individual subcase',
    detailed: 'Select a specific subcase to include in the new document. You can select multiple individual subcases.'
  },
  'duplicate-category-cancel': {
    simple: 'Cancel',
    detailed: 'Close the dialog without creating a new document. All selections will be discarded.'
  },
  'duplicate-category-confirm': {
    simple: 'Duplicate as document',
    detailed: 'Create a new document with the selected categories and subcases. The new document will be added to your document list.'
  },
  
  // SubTreeView
  'subtree-search-category': {
    simple: 'Search categories',
    detailed: 'Include category names in the search. When enabled, category names will be searched and highlighted when they match your search terms.'
  },
  'subtree-search-description': {
    simple: 'Search descriptions',
    detailed: 'Include category descriptions in the search. When enabled, category descriptions will be searched and highlighted when they match your search terms.'
  },
  'subtree-search-subcase': {
    simple: 'Search subcases',
    detailed: 'Include subcase names in the search. When enabled, subcase names will be searched and highlighted when they match your search terms.'
  },
  'subtree-search-content': {
    simple: 'Search content',
    detailed: 'Include content text in the search. When enabled, the actual content text will be searched and highlighted when it matches your search terms.'
  },
  'subtree-show-sources': {
    simple: 'Show source texts',
    detailed: 'Display source texts for content items. When enabled, you can expand content items to see the original source texts that were used to generate the content.'
  },
  'subtree-search-input': {
    simple: 'Search text',
    detailed: 'Enter search terms to find matching content. You can search for multiple words or use quotes for exact phrases. The search will highlight all matches in the category structure view.'
  },
  'subtree-clear-search': {
    simple: 'Clear search',
    detailed: 'Clear the search field and remove all search highlighting from the category structure.'
  },
  'subtree-case-sensitive': {
    simple: 'Case sensitive',
    detailed: 'Match the exact case of letters when searching. For example, "Cat" and "cat" will be treated as different when enabled.'
  },
  'subtree-whole-word': {
    simple: 'Whole word only',
    detailed: 'Only match complete words. For example, searching for "cat" will not match "category" when this option is enabled.'
  },
  'subtree-previous-match': {
    simple: 'Previous match',
    detailed: 'Navigate to the previous search match in the category structure view. This will scroll to and highlight the previous occurrence of your search terms.'
  },
  'subtree-next-match': {
    simple: 'Next match',
    detailed: 'Navigate to the next search match in the category structure view. This will scroll to and highlight the next occurrence of your search terms.'
  },
  'subtree-expand-sources': {
    simple: 'Toggle source texts',
    detailed: 'Expand or collapse the source texts for this content item. Source texts show the original text that was used to generate this content.'
  },
  
  // Common dialog buttons
  'cancel': {
    simple: 'Cancel',
    detailed: 'Close the dialog without making any changes. All unsaved changes will be discarded.'
  },
  'duplicate': {
    simple: 'Duplicate',
    detailed: 'Create a copy of the selected item with the specified settings. The duplicate will be created immediately.'
  },
  'duplicate-document-select-all': {
    simple: 'Select all subcases',
    detailed: 'Include all subcases in the duplicate document. This removes subcase filtering and includes all available subcases.'
  }
} satisfies Record<string, HelpEntry>;

export const helpContent: Record<string, HelpEntry> = helpContentDefinitions;

export type HelpContentKey = keyof typeof helpContentDefinitions;

/** Chat answer: title, Description, then Steps (visuals are shown separately when available). */
export function formatHelpChatAnswer(key: HelpContentKey): string {
  const entry = helpContent[key];
  if (!entry) return '';
  const parts: string[] = [entry.simple];
  const detail = entry.detailed?.trim();
  if (detail) {
    parts.push('', 'Description', '', detail);
  }
  if (entry.steps?.length) {
    parts.push('', 'Steps:', '');
    entry.steps.forEach((step, i) => parts.push(`${i + 1}. ${step}`));
  }
  return parts.join('\n');
}

/** @deprecated Use formatHelpChatAnswer */
export function formatHelpContext(key: HelpContentKey): string {
  return formatHelpChatAnswer(key);
}

export function formatHelpGuide(key: HelpContentKey): string {
  return formatHelpChatAnswer(key);
}

const helpTopicKeys: Record<string, HelpContentKey> = {
  // Getting started
  'how to create a new document': 'new-document',
  'how to add categories': 'add-category',
  'how to add content': 'add-content',
  'ways to create a document': 'create-document-options',
  'all ways to create a document': 'create-document-options',
  'what are all the ways to create a document': 'create-document-options',
  'what are all the ways to create a document?': 'create-document-options',
  'how can i convert a content category to a branch': 'content-category-to-branch-options',
  'how can i convert a content category to a branch?': 'content-category-to-branch-options',
  'convert content category to branch': 'content-category-to-branch-options',
  'ways to convert content category to branch': 'content-category-to-branch-options',
  'content category to branch': 'content-category-to-branch-options',

  // Documents
  'new document': 'new-document',
  'creating documents': 'create-document-options',
  'opening documents': 'open-document',
  'closing documents': 'close-document',
  'deleting documents': 'delete-document',
  'renaming documents': 'rename-document',
  'duplicating documents': 'duplicate-document',
  'import .nbr archive': 'load-from-file',
  'share with user': 'share-document',
  'merging documents': 'merge-document',
  'compare documents': 'document-merge-compare-menu',
  'undo compare copy': 'document-merge-compare-undo-last-copy',
  'undo last copy compare': 'document-merge-compare-undo-last-copy',
  'compare branch copy': 'document-merge-compare-branch-selective-copy',
  'selective branch copy compare': 'document-merge-compare-branch-selective-copy',
  'compare branch content': 'document-merge-compare-branch-content-readonly',

  // Categories
  'adding categories': 'add-category',
  'deleting categories': 'delete-category',
  'expand branch': 'category-tree-expand',
  'collapse branch': 'category-tree-expand',
  'expand category structure': 'category-tree-expand',
  'expand category tree': 'category-tree-expand',
  'double-click category structure': 'category-tree-expand',
  'double-click category tree': 'category-tree-expand',
  'splitting categories': 'split-category',
  'grouping categories': 'group-categories',
  'flattening categories': 'flatten-category',
  'collecting branch content': 'collapse-subtree-to-content-category',
  'merging categories': 'merge-categories',
  'renaming categories': 'rename-category',
  'moving categories': 'move-category',
  'drag and drop categories': 'drag-drop-categories',
  'cutting, copying, and pasting categories': 'cut-copy-paste-categories',
  'import branch from document': 'import-subtree-from-document',
  'duplicate as document': 'duplicate-as-document',
  'full-tree view': 'show-sub-tree',
  'category structure overview': 'show-sub-tree',
  'category tree overview': 'show-sub-tree',

  // Content
  'adding content': 'add-content',
  'editing content': 'edit-content',
  'deleting content': 'delete-content',
  'moving content': 'move-content',
  'move content': 'move-content',
  'drag and drop content': 'drag-drop-content',
  'cutting, copying, and pasting content': 'cut-copy-paste-content',
  'select subcase': 'select-subcase',
  'rename subcase': 'rename-subcase',
  'manage subcase groups': 'manage-subcase-groups',
  'filter content by subcase': 'content-pane-subcase-filter',
  'compare content and select': 'compare-content',
  'adjust text': 'adjust-text-content',
  'adjust content text': 'adjust-text-content',
  'borrow text from neighbouring rows': 'adjust-text-content',

  // Search
  'finding text in documents': 'find-in-document',
  'find: semantic mode': 'semantic-search',
  'search options': 'find-search-options',
  'navigation between matches': 'find-match-navigation',

  // AI
  'structure builder': 'tree-assistant',
  'structure builder, instructions (ai)': 'tree-assistant-input',
  'structure builder: instructions (ai)': 'tree-assistant-input',
  'structure builder: import tab': 'tree-assistant-outline',
  'structure builder: outline tab': 'tree-assistant-outline',
  'structure builder: bulk content tab': 'tree-assistant-bulk-rows',
  'tree assistant': 'tree-assistant',
  'tree assistant, instructions (ai)': 'tree-assistant-input',
  'tree assistant: instructions (ai)': 'tree-assistant-input',
  'tree assistant: import tab': 'tree-assistant-outline',
  'tree assistant: outline tab': 'tree-assistant-outline',
  'tree assistant: bulk content tab': 'tree-assistant-bulk-rows',
  'ask ai': 'ask-ai',
  'structure categories (ai)': 'structure-categories',
  'generate report (ai)': 'category-ai-report',
  'add matching content (ai)': 'add-matching-content',
  'new document from files (ai)': 'suggest-tree',
  'add to document from files (ai)': 'expand-document',

  // Settings
  'auto-paste settings': 'auto-paste',
  'user preferences': 'settings',
  'two-step verification': 'two-step-verification',
  'two step verification': 'two-step-verification',
  'mfa': 'two-step-verification',
  'authenticator': 'two-step-verification',

  // Quick questions
  'how do i create a new document?': 'new-document',
  'how do i load a document from a file?': 'load-from-file',
  'how do i add a category?': 'add-category',
  'how do i delete a category?': 'delete-category',
  'how do i add content to a category?': 'add-content',
  'how do i filter content by subcase?': 'content-pane-subcase-filter',
  'how do i search in my document?': 'find-in-document',
  'how do i delete a document?': 'delete-document',
  'how do i rename a document?': 'rename-document',
  'how do i use the structure builder?': 'tree-assistant',
  'how do i use the tree assistant?': 'tree-assistant',
  'what is the structure builder instructions tab?': 'tree-assistant-input',
  'what is the structure builder import tab?': 'tree-assistant-outline',
  'what is the structure builder outline tab?': 'tree-assistant-outline',
  'what is the structure builder bulk content tab?': 'tree-assistant-bulk-rows',
  'what is the tree assistant instructions tab?': 'tree-assistant-input',
  'what is the tree assistant import tab?': 'tree-assistant-outline',
  'what is the tree assistant outline tab?': 'tree-assistant-outline',
  'what is the tree assistant bulk content tab?': 'tree-assistant-bulk-rows',
  'how do i use ask ai?': 'ask-ai',
  'how do i add to document from files (ai)?': 'expand-document',
  'how do i add matching content (ai)?': 'add-matching-content',
  'how do i adjust the text of a content row?': 'adjust-text-content',
  'how do i compare content and select?': 'compare-content',
  'how do i create a new document from files (ai)?': 'suggest-tree',
  'how do i duplicate a category as a document?': 'duplicate-as-document',
  'how do i use full-tree view?': 'show-sub-tree',
  'how do i use category structure overview?': 'show-sub-tree',
  'how do i use category tree overview?': 'show-sub-tree',
  'how do i rename a subcase?': 'rename-subcase',
  'how do i manage subcase groups?': 'manage-subcase-groups',
  'how do i share a document with another user?': 'share-document',
  'how do i import a branch from another document?': 'import-subtree-from-document',
  'how do i merge categories?': 'merge-categories',
  'how do i export my document?': 'show-sub-tree',
};

/** User wants an overview of every way to add a document, not only Document Menu → New.. */
function asksForContentCategoryToBranchOverview(question: string): boolean {
  const q = question.trim().toLowerCase().replace(/\s+/g, ' ');
  const aboutContentLeaf =
    q.includes('content categor') ||
    q.includes('content bucket') ||
    q.includes('row bucket') ||
    (q.includes('leaf') && q.includes('categor'));
  const aboutBecomingBranch =
    q.includes('to branch') ||
    q.includes('into a branch') ||
    q.includes('into branch') ||
    q.includes('to a branch') ||
    q.includes('to folder') ||
    q.includes('into folder') ||
    q.includes('subcategor') ||
    q.includes('subfolder') ||
    (q.includes('branch') &&
      (q.includes('convert') || q.includes('turn') || q.includes('change') || q.includes('make')));
  if (!aboutContentLeaf && !(q.includes('convert') && q.includes('categor') && q.includes('branch'))) {
    return false;
  }
  if (!aboutBecomingBranch) {
    return false;
  }
  if (q.includes('collect') && q.includes('branch')) {
    return false;
  }
  if (
    q.includes('split') &&
    !q.includes('all ') &&
    !q.includes('option') &&
    !q.includes('way') &&
    !q.includes('method')
  ) {
    return false;
  }
  if (
    (q.includes('structure') || (q.includes('generate') && q.includes('branch'))) &&
    !q.includes('option') &&
    !q.includes('way') &&
    !q.includes('method')
  ) {
    return false;
  }
  if (
    (q.includes('structure builder') || q.includes('tree assistant')) &&
    !q.includes('option') &&
    !q.includes('way') &&
    !q.includes('method')
  ) {
    return false;
  }
  return (
    q.includes('convert') ||
    q.includes('turn') ||
    q.includes('change') ||
    q.includes('make') ||
    q.includes('how can') ||
    q.includes('how do') ||
    q.includes('all option') ||
    q.includes('all way') ||
    q.includes('all method') ||
    q.includes('different way') ||
    q.includes('different option') ||
    q.includes('other way') ||
    q.includes('what are') ||
    q.includes('ways to')
  );
}

function asksForAllDocumentCreationOptions(question: string): boolean {
  const q = question.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!q.includes('document')) {
    return false;
  }
  const wantsOverview =
    q.includes('all option') ||
    q.includes('all way') ||
    q.includes('all method') ||
    q.includes('different way') ||
    q.includes('different option') ||
    q.includes('other way') ||
    q.includes('other option') ||
    q.includes('which way') ||
    q.includes('what option') ||
    (q.includes('what are') && (q.includes('option') || q.includes('way'))) ||
    (q.includes('how many') && q.includes('way'));
  if (!wantsOverview) {
    return false;
  }
  return (
    q.includes('create') ||
    q.includes('new document') ||
    q.includes('start') ||
    q.includes('add a document') ||
    q.includes('get a document')
  );
}

/** Map a sidebar topic or free-text question to a help content key, when possible. */
export function resolveHelpContentKey(question: string): HelpContentKey | null {
  const trimmedLower = question.trim().toLowerCase();
  const direct = helpTopicKeys[trimmedLower];
  if (direct) return direct;

  if (asksForAllDocumentCreationOptions(question)) {
    return 'create-document-options';
  }

  if (asksForContentCategoryToBranchOverview(question)) {
    return 'content-category-to-branch-options';
  }

  // Duplicate as document before duplicate document
  if (
    (trimmedLower.includes('duplicate') && trimmedLower.includes('as') && trimmedLower.includes('document')) ||
    (trimmedLower.includes('duplicate') && trimmedLower.includes('category') && trimmedLower.includes('document'))
  ) {
    return 'duplicate-as-document';
  }

  // Documents
  if (trimmedLower.includes('create') && trimmedLower.includes('document')) return 'new-document';
  if (trimmedLower.includes('open') && trimmedLower.includes('document')) return 'open-document';
  if (trimmedLower.includes('close') && trimmedLower.includes('document')) return 'close-document';
  if (trimmedLower.includes('rename') && trimmedLower.includes('document')) return 'rename-document';
  if (trimmedLower.includes('delete') && trimmedLower.includes('document')) return 'delete-document';
  if (trimmedLower.includes('duplicate') && trimmedLower.includes('document')) return 'duplicate-document';
  if (
    (trimmedLower.includes('load') && (trimmedLower.includes('document') || trimmedLower.includes('file'))) ||
    (trimmedLower.includes('nbr') && trimmedLower.includes('file')) ||
    (trimmedLower.includes('import') && trimmedLower.includes('nbr'))
  ) {
    return 'load-from-file';
  }
  if (trimmedLower.includes('merge') && trimmedLower.includes('document')) return 'merge-document';
  if (
    trimmedLower.includes('undo') &&
    trimmedLower.includes('compare') &&
    (trimmedLower.includes('copy') || trimmedLower.includes('last'))
  ) {
    return 'document-merge-compare-undo-last-copy';
  }
  if (
    trimmedLower.includes('compare') &&
    trimmedLower.includes('branch') &&
    (trimmedLower.includes('subcategor') ||
      trimmedLower.includes('selective') ||
      trimmedLower.includes('one-sided') ||
      trimmedLower.includes('one sided'))
  ) {
    return 'document-merge-compare-branch-selective-copy';
  }
  if (
    trimmedLower.includes('compare') &&
    trimmedLower.includes('branch') &&
    trimmedLower.includes('content') &&
    (trimmedLower.includes('copy') || trimmedLower.includes('read'))
  ) {
    return 'document-merge-compare-branch-content-readonly';
  }
  if (
    (trimmedLower.includes('compare') && trimmedLower.includes('document')) ||
    trimmedLower.includes('merge compare') ||
    trimmedLower.includes('compare with')
  ) {
    return 'document-merge-compare-menu';
  }
  if (
    (trimmedLower.includes('share') && trimmedLower.includes('user')) ||
    (trimmedLower.includes('share') && trimmedLower.includes('document') && !trimmedLower.includes('duplicate'))
  ) {
    return 'share-document';
  }
  if (
    trimmedLower.includes('export') &&
    (trimmedLower.includes('docx') || trimmedLower.includes('word') || trimmedLower.includes('document'))
  ) {
    return 'show-sub-tree';
  }

  // AI features (specific before generic)
  if (trimmedLower.includes('matching') && trimmedLower.includes('content') && trimmedLower.includes('ai')) {
    return 'add-matching-content';
  }
  if (
    trimmedLower.includes('compare') &&
    trimmedLower.includes('content') &&
    !trimmedLower.includes('document')
  ) {
    return 'compare-content';
  }
  if (
    (trimmedLower.includes('structure') && trimmedLower.includes('categor')) ||
    (trimmedLower.includes('generate') && trimmedLower.includes('branch'))
  ) {
    return 'structure-categories';
  }
  if (trimmedLower.includes('structure builder') || trimmedLower.includes('tree assistant')) {
    if (trimmedLower.includes('import')) return 'tree-assistant-outline';
    if (trimmedLower.includes('outline')) return 'tree-assistant-outline';
    if (trimmedLower.includes('bulk')) return 'tree-assistant-bulk-rows';
    if (trimmedLower.includes('instruction')) return 'tree-assistant-input';
    return 'tree-assistant';
  }
  if (trimmedLower.includes('ask ai') || (trimmedLower.includes('sparkles') && trimmedLower.includes('document'))) {
    return 'ask-ai';
  }
  if (
    trimmedLower.includes('category report') ||
    trimmedLower.includes('generate report') ||
    (trimmedLower.includes('report') && trimmedLower.includes('ai'))
  ) {
    return 'category-ai-report';
  }
  if (
    (trimmedLower.includes('new document') || trimmedLower.includes('suggest')) &&
    trimmedLower.includes('files') &&
    !trimmedLower.includes('add to')
  ) {
    return 'suggest-tree';
  }
  if ((trimmedLower.includes('expand') || trimmedLower.includes('add to document')) && trimmedLower.includes('files')) {
    return 'expand-document';
  }

  // Categories
  if (
    (trimmedLower.includes('create') || trimmedLower.includes('add') || trimmedLower.includes('make')) &&
    trimmedLower.includes('subcategor') &&
    !trimmedLower.includes('subcase')
  ) {
    return 'add-category';
  }
  if (trimmedLower.includes('collect') && trimmedLower.includes('branch')) return 'collapse-subtree-to-content-category';
  if (trimmedLower.includes('flatten') && trimmedLower.includes('categor')) return 'flatten-category';
  if (trimmedLower.includes('group') && trimmedLower.includes('categor')) return 'group-categories';
  if (trimmedLower.includes('split') && trimmedLower.includes('categor')) return 'split-category';
  if (trimmedLower.includes('merge') && trimmedLower.includes('categor')) return 'merge-categories';
  if (trimmedLower.includes('import') && trimmedLower.includes('branch')) return 'import-subtree-from-document';
  if (
    trimmedLower.includes('full-tree') ||
    trimmedLower.includes('category structure overview') ||
    trimmedLower.includes('category structure overview') ||
    trimmedLower.includes('category structure overview') ||
    trimmedLower.includes('tree overview')
  )
    return 'show-sub-tree';
  if (
    (trimmedLower.includes('cut') || trimmedLower.includes('copy') || trimmedLower.includes('paste')) &&
    trimmedLower.includes('categor') &&
    !trimmedLower.includes('content')
  ) {
    return 'cut-copy-paste-categories';
  }
  if (trimmedLower.includes('rename') && trimmedLower.includes('categor')) return 'rename-category';
  if (trimmedLower.includes('delete') && trimmedLower.includes('categor')) return 'delete-category';
  if (
    (trimmedLower.includes('drag') || trimmedLower.includes('drop')) &&
    trimmedLower.includes('categor')
  ) {
    return 'drag-drop-categories';
  }
  if (trimmedLower.includes('move') && trimmedLower.includes('categor')) return 'move-category';
  if (trimmedLower.includes('add') && trimmedLower.includes('categor')) return 'add-category';

  // Content
  if (trimmedLower.includes('filter') && trimmedLower.includes('subcase')) return 'content-pane-subcase-filter';
  if (trimmedLower.includes('rename') && trimmedLower.includes('subcase')) return 'rename-subcase';
  if (trimmedLower.includes('manage') && trimmedLower.includes('subcase') && trimmedLower.includes('group')) {
    return 'manage-subcase-groups';
  }
  if (trimmedLower.includes('undo')) return 'undo';
  if (trimmedLower.includes('redo')) return 'redo';
  if (trimmedLower.includes('auto') && trimmedLower.includes('paste')) return 'auto-paste';
  if (trimmedLower.includes('whole word') || trimmedLower.includes('case sensitive')) {
    return 'find-search-options';
  }
  if (trimmedLower.includes('print')) return 'show-sub-tree';
  if (
    (trimmedLower.includes('cut') || trimmedLower.includes('copy') || trimmedLower.includes('paste')) &&
    trimmedLower.includes('content')
  ) {
    return 'cut-copy-paste-content';
  }
  if (
    (trimmedLower.includes('drag') || trimmedLower.includes('drop')) &&
    trimmedLower.includes('content')
  ) {
    return 'drag-drop-content';
  }
  if (trimmedLower.includes('adjust') && trimmedLower.includes('text')) return 'adjust-text-content';
  if (trimmedLower.includes('add') && trimmedLower.includes('content')) return 'add-content';
  if (trimmedLower.includes('edit') && trimmedLower.includes('content')) return 'edit-content';
  if (trimmedLower.includes('delete') && trimmedLower.includes('content')) return 'delete-content';
  if (trimmedLower.includes('move') && trimmedLower.includes('content')) return 'move-content';

  // Search
  if (trimmedLower.includes('semantic') && (trimmedLower.includes('search') || trimmedLower.includes('find'))) {
    return 'semantic-search';
  }
  if (trimmedLower.includes('search') || trimmedLower.includes('find')) {
    return 'find-in-document';
  }

  return null;
}

/** Prefer structured Description + Steps when the question maps to a help entry. */
export function formatHelpAnswer(question: string, fallbackAnswer: string): string {
  const key = resolveHelpContentKey(question);
  if (!key) return fallbackAnswer;
  const formatted = formatHelpChatAnswer(key);
  return formatted || fallbackAnswer;
} 